/**
 * Generated by @openapi-codegen
 *
 * @version 4.11.2
 */
import * as reactQuery from '@tanstack/react-query';
import { useApiContext, ApiContext } from './apiContext';
import type * as Fetcher from './apiFetcher';
import { apiFetch } from './apiFetcher';
import type * as Schemas from './apiSchemas';
import type * as Responses from './apiResponses';

export type GetVersionError = Fetcher.ErrorWrapper<undefined>;

export type GetVersionVariables = ApiContext['fetcherOptions'];

export const fetchGetVersion = (
  variables: GetVersionVariables,
  signal?: AbortSignal
) =>
  apiFetch<Schemas.Versions, GetVersionError, undefined, {}, {}, {}>({
    url: '/version',
    method: 'get',
    ...variables,
    signal,
  });

export const useGetVersion = <TData = Schemas.Versions>(
  variables: GetVersionVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Versions, GetVersionError, TData>,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useApiContext(options);
  return reactQuery.useQuery<Schemas.Versions, GetVersionError, TData>(
    queryKeyFn({ path: '/version', operationId: 'getVersion', variables }),
    ({ signal }) =>
      fetchGetVersion({ ...fetcherOptions, ...variables }, signal),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type GetDeviceVersionsQueryParams = {
  /**
   * The cursor from which to start the listing, leave empty for the first request.
   * A cursor is only valid if you use the same options/arguments for each request.
   */
  cursor?: string;
  /**
   * How many results to return? Defaults to `100`
   *
   * @minimum 10
   * @maximum 1000
   */
  count?: number;
  /**
   * What property to use to order the entries? Defaults to `name`
   */
  order_by?: Schemas.PaginationOrderBy;
  /**
   * What order should the entries have? Defaults to `ascending`
   */
  order?: Schemas.PaginationOrder;
  /**
   * Regex filter (RE2 syntax) for the name of the items, defaults to empty, which returns everything
   */
  filter?: string;
};

export type GetDeviceVersionsError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type GetDeviceVersionsVariables = {
  queryParams?: GetDeviceVersionsQueryParams;
} & ApiContext['fetcherOptions'];

export const fetchGetDeviceVersions = (
  variables: GetDeviceVersionsVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.PaginatedDeviceVersions,
    GetDeviceVersionsError,
    undefined,
    {},
    GetDeviceVersionsQueryParams,
    {}
  >({ url: '/v1/device-versions', method: 'get', ...variables, signal });

export const useGetDeviceVersions = <TData = Schemas.PaginatedDeviceVersions>(
  variables: GetDeviceVersionsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      Schemas.PaginatedDeviceVersions,
      GetDeviceVersionsError,
      TData
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useApiContext(options);
  return reactQuery.useQuery<
    Schemas.PaginatedDeviceVersions,
    GetDeviceVersionsError,
    TData
  >(
    queryKeyFn({
      path: '/v1/device-versions',
      operationId: 'getDeviceVersions',
      variables,
    }),
    ({ signal }) =>
      fetchGetDeviceVersions({ ...fetcherOptions, ...variables }, signal),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type GetRunHistoryDetailsPathParams = {
  /**
   * The ID of the Run History to retrieve.
   */
  runHistoryID: Schemas.Uuid;
};

export type GetRunHistoryDetailsQueryParams = {
  /**
   * what is the date & time of the first event to return, defaults to `7 days ago`
   *
   * @format date-time
   */
  range_start?: string;
  /**
   * what is the date & time of the last event to return, defaults to `now`
   *
   * @format date-time
   */
  range_end?: string;
  /**
   * How many results to return? Defaults to `100`
   *
   * @minimum 10
   * @maximum 1000
   */
  count?: number;
  /**
   * What order should the entries have? Defaults to `descending`
   */
  order?: Schemas.PaginationOrder;
};

export type GetRunHistoryDetailsError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type GetRunHistoryDetailsVariables = {
  pathParams: GetRunHistoryDetailsPathParams;
  queryParams?: GetRunHistoryDetailsQueryParams;
} & ApiContext['fetcherOptions'];

export const fetchGetRunHistoryDetails = (
  variables: GetRunHistoryDetailsVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.RunHistoryDetailed,
    GetRunHistoryDetailsError,
    undefined,
    {},
    GetRunHistoryDetailsQueryParams,
    GetRunHistoryDetailsPathParams
  >({
    url: '/v1/run-histories/{runHistoryID}',
    method: 'get',
    ...variables,
    signal,
  });

export const useGetRunHistoryDetails = <TData = Schemas.RunHistoryDetailed>(
  variables: GetRunHistoryDetailsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      Schemas.RunHistoryDetailed,
      GetRunHistoryDetailsError,
      TData
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useApiContext(options);
  return reactQuery.useQuery<
    Schemas.RunHistoryDetailed,
    GetRunHistoryDetailsError,
    TData
  >(
    queryKeyFn({
      path: '/v1/run-histories/{runHistoryID}',
      operationId: 'getRunHistoryDetails',
      variables,
    }),
    ({ signal }) =>
      fetchGetRunHistoryDetails({ ...fetcherOptions, ...variables }, signal),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type GetRunHistoryLogsExportPathParams = {
  /**
   * The ID of the Run History to retrieve.
   */
  runHistoryID: Schemas.Uuid;
};

export type GetRunHistoryLogsExportError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type GetRunHistoryLogsExportVariables = {
  pathParams: GetRunHistoryLogsExportPathParams;
} & ApiContext['fetcherOptions'];

export const fetchGetRunHistoryLogsExport = (
  variables: GetRunHistoryLogsExportVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.ExportAuditLogs,
    GetRunHistoryLogsExportError,
    undefined,
    {},
    {},
    GetRunHistoryLogsExportPathParams
  >({
    url: '/v1/run-histories/{runHistoryID}/logs/export',
    method: 'get',
    ...variables,
    signal,
  });

export const useGetRunHistoryLogsExport = <TData = Schemas.ExportAuditLogs>(
  variables: GetRunHistoryLogsExportVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      Schemas.ExportAuditLogs,
      GetRunHistoryLogsExportError,
      TData
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useApiContext(options);
  return reactQuery.useQuery<
    Schemas.ExportAuditLogs,
    GetRunHistoryLogsExportError,
    TData
  >(
    queryKeyFn({
      path: '/v1/run-histories/{runHistoryID}/logs/export',
      operationId: 'getRunHistoryLogsExport',
      variables,
    }),
    ({ signal }) =>
      fetchGetRunHistoryLogsExport({ ...fetcherOptions, ...variables }, signal),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type GetRunProgressPathParams = {
  /**
   * The ID of the Run History to retrieve.
   */
  runHistoryID: Schemas.Uuid;
};

export type GetRunProgressError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type GetRunProgressVariables = {
  pathParams: GetRunProgressPathParams;
} & ApiContext['fetcherOptions'];

export const fetchGetRunProgress = (
  variables: GetRunProgressVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.MaestroRunProgress,
    GetRunProgressError,
    undefined,
    {},
    {},
    GetRunProgressPathParams
  >({
    url: '/v1/run-histories/{runHistoryID}/progress',
    method: 'get',
    ...variables,
    signal,
  });

export const useGetRunProgress = <TData = Schemas.MaestroRunProgress>(
  variables: GetRunProgressVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      Schemas.MaestroRunProgress,
      GetRunProgressError,
      TData
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useApiContext(options);
  return reactQuery.useQuery<
    Schemas.MaestroRunProgress,
    GetRunProgressError,
    TData
  >(
    queryKeyFn({
      path: '/v1/run-histories/{runHistoryID}/progress',
      operationId: 'getRunProgress',
      variables,
    }),
    ({ signal }) =>
      fetchGetRunProgress({ ...fetcherOptions, ...variables }, signal),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type GetRunEventLogPathParams = {
  /**
   * The ID of the Run History to retrieve.
   */
  runHistoryID: Schemas.Uuid;
};

export type GetRunEventLogError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type GetRunEventLogVariables = {
  pathParams: GetRunEventLogPathParams;
} & ApiContext['fetcherOptions'];

export const fetchGetRunEventLog = (
  variables: GetRunEventLogVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.HubEventLogs,
    GetRunEventLogError,
    undefined,
    {},
    {},
    GetRunEventLogPathParams
  >({
    url: '/v1/run-histories/{runHistoryID}/event-log',
    method: 'get',
    ...variables,
    signal,
  });

export const useGetRunEventLog = <TData = Schemas.HubEventLogs>(
  variables: GetRunEventLogVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      Schemas.HubEventLogs,
      GetRunEventLogError,
      TData
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useApiContext(options);
  return reactQuery.useQuery<Schemas.HubEventLogs, GetRunEventLogError, TData>(
    queryKeyFn({
      path: '/v1/run-histories/{runHistoryID}/event-log',
      operationId: 'getRunEventLog',
      variables,
    }),
    ({ signal }) =>
      fetchGetRunEventLog({ ...fetcherOptions, ...variables }, signal),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type AddTagToRunHistoryPathParams = {
  /**
   * The ID of the Run History to retrieve.
   */
  runHistoryID: Schemas.Uuid;
  /**
   * The ID of the tag to affect.
   */
  tagID: Schemas.Uuid;
};

export type AddTagToRunHistoryError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type AddTagToRunHistoryVariables = {
  pathParams: AddTagToRunHistoryPathParams;
} & ApiContext['fetcherOptions'];

export const fetchAddTagToRunHistory = (
  variables: AddTagToRunHistoryVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    undefined,
    AddTagToRunHistoryError,
    undefined,
    {},
    {},
    AddTagToRunHistoryPathParams
  >({
    url: '/v1/run-histories/{runHistoryID}/tags/{tagID}',
    method: 'post',
    ...variables,
    signal,
  });

export const useAddTagToRunHistory = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      AddTagToRunHistoryError,
      AddTagToRunHistoryVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    undefined,
    AddTagToRunHistoryError,
    AddTagToRunHistoryVariables
  >(
    (variables: AddTagToRunHistoryVariables) =>
      fetchAddTagToRunHistory({ ...fetcherOptions, ...variables }),
    options
  );
};

export type RemoveTagFromRunHistoryPathParams = {
  /**
   * The ID of the Run History to retrieve.
   */
  runHistoryID: Schemas.Uuid;
  /**
   * The ID of the tag to affect.
   */
  tagID: Schemas.Uuid;
};

export type RemoveTagFromRunHistoryError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type RemoveTagFromRunHistoryVariables = {
  pathParams: RemoveTagFromRunHistoryPathParams;
} & ApiContext['fetcherOptions'];

export const fetchRemoveTagFromRunHistory = (
  variables: RemoveTagFromRunHistoryVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    undefined,
    RemoveTagFromRunHistoryError,
    undefined,
    {},
    {},
    RemoveTagFromRunHistoryPathParams
  >({
    url: '/v1/run-histories/{runHistoryID}/tags/{tagID}',
    method: 'delete',
    ...variables,
    signal,
  });

export const useRemoveTagFromRunHistory = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      RemoveTagFromRunHistoryError,
      RemoveTagFromRunHistoryVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    undefined,
    RemoveTagFromRunHistoryError,
    RemoveTagFromRunHistoryVariables
  >(
    (variables: RemoveTagFromRunHistoryVariables) =>
      fetchRemoveTagFromRunHistory({ ...fetcherOptions, ...variables }),
    options
  );
};

export type GetBatchDetailsPathParams = {
  /**
   * The ID of the Batch to retrieve.
   */
  batchID: Schemas.Uuid;
};

export type GetBatchDetailsError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type GetBatchDetailsVariables = {
  pathParams: GetBatchDetailsPathParams;
} & ApiContext['fetcherOptions'];

export const fetchGetBatchDetails = (
  variables: GetBatchDetailsVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.BatchTrace,
    GetBatchDetailsError,
    undefined,
    {},
    {},
    GetBatchDetailsPathParams
  >({ url: '/v1/batches/{batchID}', method: 'get', ...variables, signal });

export const useGetBatchDetails = <TData = Schemas.BatchTrace>(
  variables: GetBatchDetailsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.BatchTrace, GetBatchDetailsError, TData>,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useApiContext(options);
  return reactQuery.useQuery<Schemas.BatchTrace, GetBatchDetailsError, TData>(
    queryKeyFn({
      path: '/v1/batches/{batchID}',
      operationId: 'getBatchDetails',
      variables,
    }),
    ({ signal }) =>
      fetchGetBatchDetails({ ...fetcherOptions, ...variables }, signal),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type GetWorkflowDetailsPathParams = {
  /**
   * The ID of the Workflow to retrieve.
   */
  workflowID: Schemas.Uuid;
};

export type GetWorkflowDetailsError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type GetWorkflowDetailsVariables = {
  pathParams: GetWorkflowDetailsPathParams;
} & ApiContext['fetcherOptions'];

export const fetchGetWorkflowDetails = (
  variables: GetWorkflowDetailsVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.WorkflowDetailed,
    GetWorkflowDetailsError,
    undefined,
    {},
    {},
    GetWorkflowDetailsPathParams
  >({ url: '/v1/workflows/{workflowID}', method: 'get', ...variables, signal });

export const useGetWorkflowDetails = <TData = Schemas.WorkflowDetailed>(
  variables: GetWorkflowDetailsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      Schemas.WorkflowDetailed,
      GetWorkflowDetailsError,
      TData
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useApiContext(options);
  return reactQuery.useQuery<
    Schemas.WorkflowDetailed,
    GetWorkflowDetailsError,
    TData
  >(
    queryKeyFn({
      path: '/v1/workflows/{workflowID}',
      operationId: 'getWorkflowDetails',
      variables,
    }),
    ({ signal }) =>
      fetchGetWorkflowDetails({ ...fetcherOptions, ...variables }, signal),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type UpdateWorkflowPathParams = {
  /**
   * The ID of the Workflow to retrieve.
   */
  workflowID: Schemas.Uuid;
};

export type UpdateWorkflowHeaders = {
  ['X-Request-Authorization']?: string;
};

export type UpdateWorkflowError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: Responses.InvalidArguments;
    }
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type UpdateWorkflowRequestBody = Schemas.Reason & {
  payload: Schemas.WorkflowPayload;
};

export type UpdateWorkflowVariables = {
  body?: UpdateWorkflowRequestBody;
  headers?: UpdateWorkflowHeaders;
  pathParams: UpdateWorkflowPathParams;
} & ApiContext['fetcherOptions'];

export const fetchUpdateWorkflow = (
  variables: UpdateWorkflowVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.WorkflowDetailed,
    UpdateWorkflowError,
    UpdateWorkflowRequestBody,
    UpdateWorkflowHeaders,
    {},
    UpdateWorkflowPathParams
  >({ url: '/v1/workflows/{workflowID}', method: 'put', ...variables, signal });

export const useUpdateWorkflow = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.WorkflowDetailed,
      UpdateWorkflowError,
      UpdateWorkflowVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    Schemas.WorkflowDetailed,
    UpdateWorkflowError,
    UpdateWorkflowVariables
  >(
    (variables: UpdateWorkflowVariables) =>
      fetchUpdateWorkflow({ ...fetcherOptions, ...variables }),
    options
  );
};

export type DeleteWorkflowPathParams = {
  /**
   * The ID of the Workflow to retrieve.
   */
  workflowID: Schemas.Uuid;
};

export type DeleteWorkflowHeaders = {
  ['X-Request-Authorization']?: string;
};

export type DeleteWorkflowError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type DeleteWorkflowVariables = {
  body: Schemas.Reason;
  headers?: DeleteWorkflowHeaders;
  pathParams: DeleteWorkflowPathParams;
} & ApiContext['fetcherOptions'];

export const fetchDeleteWorkflow = (
  variables: DeleteWorkflowVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    undefined,
    DeleteWorkflowError,
    Schemas.Reason,
    DeleteWorkflowHeaders,
    {},
    DeleteWorkflowPathParams
  >({
    url: '/v1/workflows/{workflowID}',
    method: 'delete',
    ...variables,
    signal,
  });

export const useDeleteWorkflow = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      DeleteWorkflowError,
      DeleteWorkflowVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    undefined,
    DeleteWorkflowError,
    DeleteWorkflowVariables
  >(
    (variables: DeleteWorkflowVariables) =>
      fetchDeleteWorkflow({ ...fetcherOptions, ...variables }),
    options
  );
};

export type DuplicateWorkflowPathParams = {
  /**
   * The ID of the Workflow to retrieve.
   */
  workflowID: Schemas.Uuid;
};

export type DuplicateWorkflowHeaders = {
  ['X-Request-Authorization']?: string;
};

export type DuplicateWorkflowError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: Responses.InvalidArguments;
    }
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type DuplicateWorkflowVariables = {
  body: Schemas.DuplicateResourcePayload;
  headers?: DuplicateWorkflowHeaders;
  pathParams: DuplicateWorkflowPathParams;
} & ApiContext['fetcherOptions'];

export const fetchDuplicateWorkflow = (
  variables: DuplicateWorkflowVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.Workflow,
    DuplicateWorkflowError,
    Schemas.DuplicateResourcePayload,
    DuplicateWorkflowHeaders,
    {},
    DuplicateWorkflowPathParams
  >({
    url: '/v1/workflows/{workflowID}/duplicate',
    method: 'post',
    ...variables,
    signal,
  });

export const useDuplicateWorkflow = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Workflow,
      DuplicateWorkflowError,
      DuplicateWorkflowVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    Schemas.Workflow,
    DuplicateWorkflowError,
    DuplicateWorkflowVariables
  >(
    (variables: DuplicateWorkflowVariables) =>
      fetchDuplicateWorkflow({ ...fetcherOptions, ...variables }),
    options
  );
};

export type RunInstructionsCommitPathParams = {
  /**
   * The ID of the Workflow to retrieve.
   */
  workflowID: Schemas.Uuid;
  /**
   * The type of the run instructions to affect.
   */
  runInstructionType: Schemas.RunInstructionType;
};

export type RunInstructionsCommitError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: Responses.InvalidArguments;
    }
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type RunInstructionsCommitVariables = {
  pathParams: RunInstructionsCommitPathParams;
} & ApiContext['fetcherOptions'];

export const fetchRunInstructionsCommit = (
  variables: RunInstructionsCommitVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    undefined,
    RunInstructionsCommitError,
    undefined,
    {},
    {},
    RunInstructionsCommitPathParams
  >({
    url: '/v1/workflows/{workflowID}/run-instructions/{runInstructionType}/commit',
    method: 'post',
    ...variables,
    signal,
  });

export const useRunInstructionsCommit = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      RunInstructionsCommitError,
      RunInstructionsCommitVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    undefined,
    RunInstructionsCommitError,
    RunInstructionsCommitVariables
  >(
    (variables: RunInstructionsCommitVariables) =>
      fetchRunInstructionsCommit({ ...fetcherOptions, ...variables }),
    options
  );
};

export type RunInstructionsSkipPathParams = {
  /**
   * The ID of the Workflow to retrieve.
   */
  workflowID: Schemas.Uuid;
  /**
   * The type of the run instructions to affect.
   */
  runInstructionType: Schemas.RunInstructionType;
};

export type RunInstructionsSkipError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: Responses.InvalidArguments;
    }
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type RunInstructionsSkipVariables = {
  pathParams: RunInstructionsSkipPathParams;
} & ApiContext['fetcherOptions'];

export const fetchRunInstructionsSkip = (
  variables: RunInstructionsSkipVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    undefined,
    RunInstructionsSkipError,
    undefined,
    {},
    {},
    RunInstructionsSkipPathParams
  >({
    url: '/v1/workflows/{workflowID}/run-instructions/{runInstructionType}/skip',
    method: 'post',
    ...variables,
    signal,
  });

export const useRunInstructionsSkip = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      RunInstructionsSkipError,
      RunInstructionsSkipVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    undefined,
    RunInstructionsSkipError,
    RunInstructionsSkipVariables
  >(
    (variables: RunInstructionsSkipVariables) =>
      fetchRunInstructionsSkip({ ...fetcherOptions, ...variables }),
    options
  );
};

export type AddTagToWorkflowPathParams = {
  /**
   * The ID of the Workflow to retrieve.
   */
  workflowID: Schemas.Uuid;
  /**
   * The ID of the tag to affect.
   */
  tagID: Schemas.Uuid;
};

export type AddTagToWorkflowError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type AddTagToWorkflowVariables = {
  pathParams: AddTagToWorkflowPathParams;
} & ApiContext['fetcherOptions'];

export const fetchAddTagToWorkflow = (
  variables: AddTagToWorkflowVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    undefined,
    AddTagToWorkflowError,
    undefined,
    {},
    {},
    AddTagToWorkflowPathParams
  >({
    url: '/v1/workflows/{workflowID}/tags/{tagID}',
    method: 'post',
    ...variables,
    signal,
  });

export const useAddTagToWorkflow = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      AddTagToWorkflowError,
      AddTagToWorkflowVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    undefined,
    AddTagToWorkflowError,
    AddTagToWorkflowVariables
  >(
    (variables: AddTagToWorkflowVariables) =>
      fetchAddTagToWorkflow({ ...fetcherOptions, ...variables }),
    options
  );
};

export type RemoveTagFromWorkflowPathParams = {
  /**
   * The ID of the Workflow to retrieve.
   */
  workflowID: Schemas.Uuid;
  /**
   * The ID of the tag to affect.
   */
  tagID: Schemas.Uuid;
};

export type RemoveTagFromWorkflowError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type RemoveTagFromWorkflowVariables = {
  pathParams: RemoveTagFromWorkflowPathParams;
} & ApiContext['fetcherOptions'];

export const fetchRemoveTagFromWorkflow = (
  variables: RemoveTagFromWorkflowVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    undefined,
    RemoveTagFromWorkflowError,
    undefined,
    {},
    {},
    RemoveTagFromWorkflowPathParams
  >({
    url: '/v1/workflows/{workflowID}/tags/{tagID}',
    method: 'delete',
    ...variables,
    signal,
  });

export const useRemoveTagFromWorkflow = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      RemoveTagFromWorkflowError,
      RemoveTagFromWorkflowVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    undefined,
    RemoveTagFromWorkflowError,
    RemoveTagFromWorkflowVariables
  >(
    (variables: RemoveTagFromWorkflowVariables) =>
      fetchRemoveTagFromWorkflow({ ...fetcherOptions, ...variables }),
    options
  );
};

export type GetMaestroVersionsError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type GetMaestroVersionsVariables = ApiContext['fetcherOptions'];

export const fetchGetMaestroVersions = (
  variables: GetMaestroVersionsVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.MaestroVersions,
    GetMaestroVersionsError,
    undefined,
    {},
    {},
    {}
  >({ url: '/v1/maestro/versions', method: 'get', ...variables, signal });

export const useGetMaestroVersions = <TData = Schemas.MaestroVersions>(
  variables: GetMaestroVersionsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      Schemas.MaestroVersions,
      GetMaestroVersionsError,
      TData
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useApiContext(options);
  return reactQuery.useQuery<
    Schemas.MaestroVersions,
    GetMaestroVersionsError,
    TData
  >(
    queryKeyFn({
      path: '/v1/maestro/versions',
      operationId: 'getMaestroVersions',
      variables,
    }),
    ({ signal }) =>
      fetchGetMaestroVersions({ ...fetcherOptions, ...variables }, signal),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type GetEvalVersionsError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type GetEvalVersionsVariables = ApiContext['fetcherOptions'];

export const fetchGetEvalVersions = (
  variables: GetEvalVersionsVariables,
  signal?: AbortSignal
) =>
  apiFetch<Schemas.EvalVersions, GetEvalVersionsError, undefined, {}, {}, {}>({
    url: '/v1/evals/versions',
    method: 'get',
    ...variables,
    signal,
  });

export const useGetEvalVersions = <TData = Schemas.EvalVersions>(
  variables: GetEvalVersionsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      Schemas.EvalVersions,
      GetEvalVersionsError,
      TData
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useApiContext(options);
  return reactQuery.useQuery<Schemas.EvalVersions, GetEvalVersionsError, TData>(
    queryKeyFn({
      path: '/v1/evals/versions',
      operationId: 'getEvalVersions',
      variables,
    }),
    ({ signal }) =>
      fetchGetEvalVersions({ ...fetcherOptions, ...variables }, signal),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type GetArchetypeVersionsError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type GetArchetypeVersionsVariables = ApiContext['fetcherOptions'];

export const fetchGetArchetypeVersions = (
  variables: GetArchetypeVersionsVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.ArchetypeVersions,
    GetArchetypeVersionsError,
    undefined,
    {},
    {},
    {}
  >({ url: '/v1/archetypes/versions', method: 'get', ...variables, signal });

export const useGetArchetypeVersions = <TData = Schemas.ArchetypeVersions>(
  variables: GetArchetypeVersionsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      Schemas.ArchetypeVersions,
      GetArchetypeVersionsError,
      TData
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useApiContext(options);
  return reactQuery.useQuery<
    Schemas.ArchetypeVersions,
    GetArchetypeVersionsError,
    TData
  >(
    queryKeyFn({
      path: '/v1/archetypes/versions',
      operationId: 'getArchetypeVersions',
      variables,
    }),
    ({ signal }) =>
      fetchGetArchetypeVersions({ ...fetcherOptions, ...variables }, signal),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type GetDeviceDataPathParams = {
  /**
   * The ID or serial of the device to affect.
   */
  deviceIDOrSerial: Schemas.Uuid | Schemas.RequiredString;
};

export type GetDeviceDataQueryParams = {
  /**
   * what is the date & time of the first event to return, defaults to `7 days ago`
   *
   * @format date-time
   */
  range_start?: string;
  /**
   * what is the date & time of the last event to return, defaults to `now`
   *
   * @format date-time
   */
  range_end?: string;
  /**
   * How many results to return? Defaults to `100`
   *
   * @minimum 10
   * @maximum 1000
   */
  count?: number;
  /**
   * What order should the entries have? Defaults to `descending`
   */
  order?: Schemas.PaginationOrder;
};

export type GetDeviceDataError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type GetDeviceDataVariables = {
  pathParams: GetDeviceDataPathParams;
  queryParams?: GetDeviceDataQueryParams;
} & ApiContext['fetcherOptions'];

export const fetchGetDeviceData = (
  variables: GetDeviceDataVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.DeviceData,
    GetDeviceDataError,
    undefined,
    {},
    GetDeviceDataQueryParams,
    GetDeviceDataPathParams
  >({
    url: '/v1/devices/{deviceIDOrSerial}',
    method: 'get',
    ...variables,
    signal,
  });

export const useGetDeviceData = <TData = Schemas.DeviceData>(
  variables: GetDeviceDataVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.DeviceData, GetDeviceDataError, TData>,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useApiContext(options);
  return reactQuery.useQuery<Schemas.DeviceData, GetDeviceDataError, TData>(
    queryKeyFn({
      path: '/v1/devices/{deviceIDOrSerial}',
      operationId: 'getDeviceData',
      variables,
    }),
    ({ signal }) =>
      fetchGetDeviceData({ ...fetcherOptions, ...variables }, signal),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type GetDeviceStateHistoryPathParams = {
  /**
   * The ID of the device to affect.
   */
  deviceID: Schemas.Uuid;
};

export type GetDeviceStateHistoryQueryParams = {
  /**
   * what is the date & time of the first event to return, defaults to `7 days ago`
   *
   * @format date-time
   */
  range_start?: string;
  /**
   * what is the date & time of the last event to return, defaults to `now`
   *
   * @format date-time
   */
  range_end?: string;
  /**
   * The cursor from which to start the listing, leave empty for the first request.
   * A cursor is only valid if you use the same options/arguments for each request.
   */
  cursor?: string;
  /**
   * How many results to return? Defaults to `100`
   *
   * @minimum 10
   * @maximum 1000
   */
  count?: number;
  /**
   * What order should the entries have? Defaults to `descending`
   */
  order?: Schemas.PaginationOrder;
  /**
   * The ID of the Run History to filter by.
   */
  run_history_id?: Schemas.Uuid;
};

export type GetDeviceStateHistoryError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: Responses.WrongRangeError;
    }
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type GetDeviceStateHistoryVariables = {
  pathParams: GetDeviceStateHistoryPathParams;
  queryParams?: GetDeviceStateHistoryQueryParams;
} & ApiContext['fetcherOptions'];

export const fetchGetDeviceStateHistory = (
  variables: GetDeviceStateHistoryVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.PaginatedDeviceDataStates,
    GetDeviceStateHistoryError,
    undefined,
    {},
    GetDeviceStateHistoryQueryParams,
    GetDeviceStateHistoryPathParams
  >({
    url: '/v1/devices/{deviceID}/history/state',
    method: 'get',
    ...variables,
    signal,
  });

export const useGetDeviceStateHistory = <
  TData = Schemas.PaginatedDeviceDataStates
>(
  variables: GetDeviceStateHistoryVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      Schemas.PaginatedDeviceDataStates,
      GetDeviceStateHistoryError,
      TData
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useApiContext(options);
  return reactQuery.useQuery<
    Schemas.PaginatedDeviceDataStates,
    GetDeviceStateHistoryError,
    TData
  >(
    queryKeyFn({
      path: '/v1/devices/{deviceID}/history/state',
      operationId: 'getDeviceStateHistory',
      variables,
    }),
    ({ signal }) =>
      fetchGetDeviceStateHistory({ ...fetcherOptions, ...variables }, signal),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type GetDeviceMetricsHistoryPathParams = {
  /**
   * The ID of the device to affect.
   */
  deviceID: Schemas.Uuid;
};

export type GetDeviceMetricsHistoryQueryParams = {
  /**
   * what is the date & time of the first event to return, defaults to `7 days ago`
   *
   * @format date-time
   */
  range_start?: string;
  /**
   * what is the date & time of the last event to return, defaults to `now`
   *
   * @format date-time
   */
  range_end?: string;
  /**
   * The cursor from which to start the listing, leave empty for the first request.
   * A cursor is only valid if you use the same options/arguments for each request.
   */
  cursor?: string;
  /**
   * How many results to return? Defaults to `100`
   *
   * @minimum 10
   * @maximum 1000
   */
  count?: number;
  /**
   * What order should the entries have? Defaults to `descending`
   */
  order?: Schemas.PaginationOrder;
  metrics?: string[];
  /**
   * The ID of the Run History to filter by.
   */
  run_history_id?: Schemas.Uuid;
};

export type GetDeviceMetricsHistoryError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: Responses.WrongRangeError;
    }
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type GetDeviceMetricsHistoryVariables = {
  pathParams: GetDeviceMetricsHistoryPathParams;
  queryParams?: GetDeviceMetricsHistoryQueryParams;
} & ApiContext['fetcherOptions'];

export const fetchGetDeviceMetricsHistory = (
  variables: GetDeviceMetricsHistoryVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.PaginatedDeviceDataMetrics,
    GetDeviceMetricsHistoryError,
    undefined,
    {},
    GetDeviceMetricsHistoryQueryParams,
    GetDeviceMetricsHistoryPathParams
  >({
    url: '/v1/devices/{deviceID}/history/metrics',
    method: 'get',
    ...variables,
    signal,
  });

export const useGetDeviceMetricsHistory = <
  TData = Schemas.PaginatedDeviceDataMetrics
>(
  variables: GetDeviceMetricsHistoryVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      Schemas.PaginatedDeviceDataMetrics,
      GetDeviceMetricsHistoryError,
      TData
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useApiContext(options);
  return reactQuery.useQuery<
    Schemas.PaginatedDeviceDataMetrics,
    GetDeviceMetricsHistoryError,
    TData
  >(
    queryKeyFn({
      path: '/v1/devices/{deviceID}/history/metrics',
      operationId: 'getDeviceMetricsHistory',
      variables,
    }),
    ({ signal }) =>
      fetchGetDeviceMetricsHistory({ ...fetcherOptions, ...variables }, signal),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type GetDeviceLogsHistoryPathParams = {
  /**
   * The ID of the device to affect.
   */
  deviceID: Schemas.Uuid;
};

export type GetDeviceLogsHistoryQueryParams = {
  /**
   * what is the date & time of the first event to return, defaults to `7 days ago`
   *
   * @format date-time
   */
  range_start?: string;
  /**
   * what is the date & time of the last event to return, defaults to `now`
   *
   * @format date-time
   */
  range_end?: string;
  /**
   * The cursor from which to start the listing, leave empty for the first request.
   * A cursor is only valid if you use the same options/arguments for each request.
   */
  cursor?: string;
  /**
   * How many results to return? Defaults to `100`
   *
   * @minimum 10
   * @maximum 1000
   */
  count?: number;
  /**
   * What order should the entries have? Defaults to `descending`
   */
  order?: Schemas.PaginationOrder;
  filter?: string;
  /**
   * The ID of the Run History to filter by.
   */
  run_history_id?: Schemas.Uuid;
};

export type GetDeviceLogsHistoryError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: Responses.WrongRangeError;
    }
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type GetDeviceLogsHistoryVariables = {
  pathParams: GetDeviceLogsHistoryPathParams;
  queryParams?: GetDeviceLogsHistoryQueryParams;
} & ApiContext['fetcherOptions'];

export const fetchGetDeviceLogsHistory = (
  variables: GetDeviceLogsHistoryVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.PaginatedDeviceDataLogs,
    GetDeviceLogsHistoryError,
    undefined,
    {},
    GetDeviceLogsHistoryQueryParams,
    GetDeviceLogsHistoryPathParams
  >({
    url: '/v1/devices/{deviceID}/history/logs',
    method: 'get',
    ...variables,
    signal,
  });

export const useGetDeviceLogsHistory = <
  TData = Schemas.PaginatedDeviceDataLogs
>(
  variables: GetDeviceLogsHistoryVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      Schemas.PaginatedDeviceDataLogs,
      GetDeviceLogsHistoryError,
      TData
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useApiContext(options);
  return reactQuery.useQuery<
    Schemas.PaginatedDeviceDataLogs,
    GetDeviceLogsHistoryError,
    TData
  >(
    queryKeyFn({
      path: '/v1/devices/{deviceID}/history/logs',
      operationId: 'getDeviceLogsHistory',
      variables,
    }),
    ({ signal }) =>
      fetchGetDeviceLogsHistory({ ...fetcherOptions, ...variables }, signal),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type GetDeviceRunHistoriesHistoryPathParams = {
  /**
   * The ID of the device to affect.
   */
  deviceID: Schemas.Uuid;
};

export type GetDeviceRunHistoriesHistoryQueryParams = {
  /**
   * what is the date & time of the first event to return, defaults to `7 days ago`
   *
   * @format date-time
   */
  range_start?: string;
  /**
   * what is the date & time of the last event to return, defaults to `now`
   *
   * @format date-time
   */
  range_end?: string;
  /**
   * The cursor from which to start the listing, leave empty for the first request.
   * A cursor is only valid if you use the same options/arguments for each request.
   */
  cursor?: string;
  /**
   * How many results to return? Defaults to `100`
   *
   * @minimum 10
   * @maximum 1000
   */
  count?: number;
  /**
   * What order should the entries have? Defaults to `descending`
   */
  order?: Schemas.PaginationOrder;
  /**
   * The tag to filter by.
   */
  tags?: Schemas.Uuid[];
};

export type GetDeviceRunHistoriesHistoryError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: Responses.WrongRangeError;
    }
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type GetDeviceRunHistoriesHistoryVariables = {
  pathParams: GetDeviceRunHistoriesHistoryPathParams;
  queryParams?: GetDeviceRunHistoriesHistoryQueryParams;
} & ApiContext['fetcherOptions'];

export const fetchGetDeviceRunHistoriesHistory = (
  variables: GetDeviceRunHistoriesHistoryVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.PaginatedDeviceRunHistories,
    GetDeviceRunHistoriesHistoryError,
    undefined,
    {},
    GetDeviceRunHistoriesHistoryQueryParams,
    GetDeviceRunHistoriesHistoryPathParams
  >({
    url: '/v1/devices/{deviceID}/history/run-histories',
    method: 'get',
    ...variables,
    signal,
  });

export const useGetDeviceRunHistoriesHistory = <
  TData = Schemas.PaginatedDeviceRunHistories
>(
  variables: GetDeviceRunHistoriesHistoryVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      Schemas.PaginatedDeviceRunHistories,
      GetDeviceRunHistoriesHistoryError,
      TData
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useApiContext(options);
  return reactQuery.useQuery<
    Schemas.PaginatedDeviceRunHistories,
    GetDeviceRunHistoriesHistoryError,
    TData
  >(
    queryKeyFn({
      path: '/v1/devices/{deviceID}/history/run-histories',
      operationId: 'getDeviceRunHistoriesHistory',
      variables,
    }),
    ({ signal }) =>
      fetchGetDeviceRunHistoriesHistory(
        { ...fetcherOptions, ...variables },
        signal
      ),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type RenameDevicePathParams = {
  /**
   * The ID of the device to affect.
   */
  deviceID: Schemas.Uuid;
};

export type RenameDeviceHeaders = {
  ['X-Request-Authorization']?: string;
};

export type RenameDeviceError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: Responses.DuplicateError;
    }
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type RenameDeviceVariables = {
  body: Schemas.RenamePayload;
  headers?: RenameDeviceHeaders;
  pathParams: RenameDevicePathParams;
} & ApiContext['fetcherOptions'];

export const fetchRenameDevice = (
  variables: RenameDeviceVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    undefined,
    RenameDeviceError,
    Schemas.RenamePayload,
    RenameDeviceHeaders,
    {},
    RenameDevicePathParams
  >({
    url: '/v1/devices/{deviceID}/rename',
    method: 'put',
    ...variables,
    signal,
  });

export const useRenameDevice = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      RenameDeviceError,
      RenameDeviceVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    undefined,
    RenameDeviceError,
    RenameDeviceVariables
  >(
    (variables: RenameDeviceVariables) =>
      fetchRenameDevice({ ...fetcherOptions, ...variables }),
    options
  );
};

export type LoadWorkflowOnDevicePathParams = {
  /**
   * The ID of the device to affect.
   */
  deviceID: Schemas.Uuid;
};

export type LoadWorkflowOnDeviceError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: Responses.WrongStateError;
    }
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type LoadWorkflowOnDeviceRequestBody = Schemas.Reason & {
  payload: Schemas.LoadPayload;
  /**
   * @format binary
   */
  file?: Blob;
};

export type LoadWorkflowOnDeviceVariables = {
  body?: LoadWorkflowOnDeviceRequestBody;
  pathParams: LoadWorkflowOnDevicePathParams;
} & ApiContext['fetcherOptions'];

export const fetchLoadWorkflowOnDevice = (
  variables: LoadWorkflowOnDeviceVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    undefined,
    LoadWorkflowOnDeviceError,
    LoadWorkflowOnDeviceRequestBody,
    {},
    {},
    LoadWorkflowOnDevicePathParams
  >({
    url: '/v1/devices/{deviceID}/state/load',
    method: 'post',
    ...variables,
    signal,
  });

export const useLoadWorkflowOnDevice = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      LoadWorkflowOnDeviceError,
      LoadWorkflowOnDeviceVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    undefined,
    LoadWorkflowOnDeviceError,
    LoadWorkflowOnDeviceVariables
  >(
    (variables: LoadWorkflowOnDeviceVariables) =>
      fetchLoadWorkflowOnDevice({ ...fetcherOptions, ...variables }),
    options
  );
};

export type DeployWorkflowToDevicePathParams = {
  /**
   * The ID of the device to affect.
   */
  deviceID: Schemas.Uuid;
};

export type DeployWorkflowToDeviceError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: Responses.WrongStateError;
    }
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type DeployWorkflowToDeviceRequestBody = Schemas.Reason & {
  payload: Schemas.LoadPayload;
  parameter_files?: Blob[];
  reference_data_files?: Blob[];
};

export type DeployWorkflowToDeviceVariables = {
  body?: DeployWorkflowToDeviceRequestBody;
  pathParams: DeployWorkflowToDevicePathParams;
} & ApiContext['fetcherOptions'];

export const fetchDeployWorkflowToDevice = (
  variables: DeployWorkflowToDeviceVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    undefined,
    DeployWorkflowToDeviceError,
    DeployWorkflowToDeviceRequestBody,
    {},
    {},
    DeployWorkflowToDevicePathParams
  >({
    url: '/v1/devices/{deviceID}/deploy',
    method: 'post',
    ...variables,
    signal,
  });

export const useDeployWorkflowToDevice = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      DeployWorkflowToDeviceError,
      DeployWorkflowToDeviceVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    undefined,
    DeployWorkflowToDeviceError,
    DeployWorkflowToDeviceVariables
  >(
    (variables: DeployWorkflowToDeviceVariables) =>
      fetchDeployWorkflowToDevice({ ...fetcherOptions, ...variables }),
    options
  );
};

export type UpdateDeviceSoftwarePathParams = {
  /**
   * The ID of the device to affect.
   */
  deviceID: Schemas.Uuid;
};

export type UpdateDeviceSoftwareError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: Responses.WrongStateError;
    }
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type UpdateDeviceSoftwareVariables = {
  body: Schemas.UpdatePayload;
  pathParams: UpdateDeviceSoftwarePathParams;
} & ApiContext['fetcherOptions'];

export const fetchUpdateDeviceSoftware = (
  variables: UpdateDeviceSoftwareVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    undefined,
    UpdateDeviceSoftwareError,
    Schemas.UpdatePayload,
    {},
    {},
    UpdateDeviceSoftwarePathParams
  >({
    url: '/v1/devices/{deviceID}/state/update',
    method: 'post',
    ...variables,
    signal,
  });

export const useUpdateDeviceSoftware = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      UpdateDeviceSoftwareError,
      UpdateDeviceSoftwareVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    undefined,
    UpdateDeviceSoftwareError,
    UpdateDeviceSoftwareVariables
  >(
    (variables: UpdateDeviceSoftwareVariables) =>
      fetchUpdateDeviceSoftware({ ...fetcherOptions, ...variables }),
    options
  );
};

export type GetDeviceConfigurationPathParams = {
  /**
   * The ID of the device to affect.
   */
  deviceID: Schemas.Uuid;
};

export type GetDeviceConfigurationError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: Responses.WrongStateError;
    }
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type GetDeviceConfigurationVariables = {
  pathParams: GetDeviceConfigurationPathParams;
} & ApiContext['fetcherOptions'];

export const fetchGetDeviceConfiguration = (
  variables: GetDeviceConfigurationVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.DeviceConfiguration,
    GetDeviceConfigurationError,
    undefined,
    {},
    {},
    GetDeviceConfigurationPathParams
  >({
    url: '/v1/devices/{deviceID}/state/configure',
    method: 'get',
    ...variables,
    signal,
  });

export const useGetDeviceConfiguration = <TData = Schemas.DeviceConfiguration>(
  variables: GetDeviceConfigurationVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      Schemas.DeviceConfiguration,
      GetDeviceConfigurationError,
      TData
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useApiContext(options);
  return reactQuery.useQuery<
    Schemas.DeviceConfiguration,
    GetDeviceConfigurationError,
    TData
  >(
    queryKeyFn({
      path: '/v1/devices/{deviceID}/state/configure',
      operationId: 'getDeviceConfiguration',
      variables,
    }),
    ({ signal }) =>
      fetchGetDeviceConfiguration({ ...fetcherOptions, ...variables }, signal),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type ConfigureDevicePathParams = {
  /**
   * The ID of the device to affect.
   */
  deviceID: Schemas.Uuid;
};

export type ConfigureDeviceError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: Responses.WrongStateError;
    }
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type ConfigureDeviceRequestBody = Schemas.Reason & {
  payload: Schemas.ConfigurePayload;
};

export type ConfigureDeviceVariables = {
  body?: ConfigureDeviceRequestBody;
  pathParams: ConfigureDevicePathParams;
} & ApiContext['fetcherOptions'];

export const fetchConfigureDevice = (
  variables: ConfigureDeviceVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    undefined,
    ConfigureDeviceError,
    ConfigureDeviceRequestBody,
    {},
    {},
    ConfigureDevicePathParams
  >({
    url: '/v1/devices/{deviceID}/state/configure',
    method: 'post',
    ...variables,
    signal,
  });

export const useConfigureDevice = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      ConfigureDeviceError,
      ConfigureDeviceVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    undefined,
    ConfigureDeviceError,
    ConfigureDeviceVariables
  >(
    (variables: ConfigureDeviceVariables) =>
      fetchConfigureDevice({ ...fetcherOptions, ...variables }),
    options
  );
};

export type StartDevicePathParams = {
  /**
   * The ID of the device to affect.
   */
  deviceID: Schemas.Uuid;
};

export type StartDeviceError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: Responses.WrongStateError;
    }
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type StartDeviceRequestBody = Schemas.Reason & {
  payload: Schemas.StartParameters;
};

export type StartDeviceVariables = {
  body?: StartDeviceRequestBody;
  pathParams: StartDevicePathParams;
} & ApiContext['fetcherOptions'];

export const fetchStartDevice = (
  variables: StartDeviceVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    undefined,
    StartDeviceError,
    StartDeviceRequestBody,
    {},
    {},
    StartDevicePathParams
  >({
    url: '/v1/devices/{deviceID}/state/start',
    method: 'post',
    ...variables,
    signal,
  });

export const useStartDevice = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      StartDeviceError,
      StartDeviceVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    undefined,
    StartDeviceError,
    StartDeviceVariables
  >(
    (variables: StartDeviceVariables) =>
      fetchStartDevice({ ...fetcherOptions, ...variables }),
    options
  );
};

export type PauseDevicePathParams = {
  /**
   * The ID of the device to affect.
   */
  deviceID: Schemas.Uuid;
};

export type PauseDeviceError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: Responses.WrongStateError;
    }
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type PauseDeviceVariables = {
  body: Schemas.Reason;
  pathParams: PauseDevicePathParams;
} & ApiContext['fetcherOptions'];

export const fetchPauseDevice = (
  variables: PauseDeviceVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    undefined,
    PauseDeviceError,
    Schemas.Reason,
    {},
    {},
    PauseDevicePathParams
  >({
    url: '/v1/devices/{deviceID}/state/pause',
    method: 'post',
    ...variables,
    signal,
  });

export const usePauseDevice = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      PauseDeviceError,
      PauseDeviceVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    undefined,
    PauseDeviceError,
    PauseDeviceVariables
  >(
    (variables: PauseDeviceVariables) =>
      fetchPauseDevice({ ...fetcherOptions, ...variables }),
    options
  );
};

export type ConfirmPauseDevicePathParams = {
  /**
   * The ID of the device to affect.
   */
  deviceID: Schemas.Uuid;
};

export type ConfirmPauseDeviceError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: Responses.WrongStateError;
    }
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type ConfirmPauseDeviceVariables = {
  pathParams: ConfirmPauseDevicePathParams;
} & ApiContext['fetcherOptions'];

export const fetchConfirmPauseDevice = (
  variables: ConfirmPauseDeviceVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    undefined,
    ConfirmPauseDeviceError,
    undefined,
    {},
    {},
    ConfirmPauseDevicePathParams
  >({
    url: '/v1/devices/{deviceID}/state/confirm-pause',
    method: 'post',
    ...variables,
    signal,
  });

export const useConfirmPauseDevice = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      ConfirmPauseDeviceError,
      ConfirmPauseDeviceVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    undefined,
    ConfirmPauseDeviceError,
    ConfirmPauseDeviceVariables
  >(
    (variables: ConfirmPauseDeviceVariables) =>
      fetchConfirmPauseDevice({ ...fetcherOptions, ...variables }),
    options
  );
};

export type CancelPauseDevicePathParams = {
  /**
   * The ID of the device to affect.
   */
  deviceID: Schemas.Uuid;
};

export type CancelPauseDeviceError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: Responses.WrongStateError;
    }
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type CancelPauseDeviceVariables = {
  pathParams: CancelPauseDevicePathParams;
} & ApiContext['fetcherOptions'];

export const fetchCancelPauseDevice = (
  variables: CancelPauseDeviceVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    undefined,
    CancelPauseDeviceError,
    undefined,
    {},
    {},
    CancelPauseDevicePathParams
  >({
    url: '/v1/devices/{deviceID}/state/cancel-pause',
    method: 'post',
    ...variables,
    signal,
  });

export const useCancelPauseDevice = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      CancelPauseDeviceError,
      CancelPauseDeviceVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    undefined,
    CancelPauseDeviceError,
    CancelPauseDeviceVariables
  >(
    (variables: CancelPauseDeviceVariables) =>
      fetchCancelPauseDevice({ ...fetcherOptions, ...variables }),
    options
  );
};

export type ResumeDevicePathParams = {
  /**
   * The ID of the device to affect.
   */
  deviceID: Schemas.Uuid;
};

export type ResumeDeviceError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: Responses.WrongStateError;
    }
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type ResumeDeviceVariables = {
  body: Schemas.Reason;
  pathParams: ResumeDevicePathParams;
} & ApiContext['fetcherOptions'];

export const fetchResumeDevice = (
  variables: ResumeDeviceVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    undefined,
    ResumeDeviceError,
    Schemas.Reason,
    {},
    {},
    ResumeDevicePathParams
  >({
    url: '/v1/devices/{deviceID}/state/resume',
    method: 'post',
    ...variables,
    signal,
  });

export const useResumeDevice = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      ResumeDeviceError,
      ResumeDeviceVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    undefined,
    ResumeDeviceError,
    ResumeDeviceVariables
  >(
    (variables: ResumeDeviceVariables) =>
      fetchResumeDevice({ ...fetcherOptions, ...variables }),
    options
  );
};

export type StopDevicePathParams = {
  /**
   * The ID of the device to affect.
   */
  deviceID: Schemas.Uuid;
};

export type StopDeviceError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: Responses.WrongStateError;
    }
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type StopDeviceRequestBody = Schemas.Reason & {
  payload: Schemas.StopReason;
};

export type StopDeviceVariables = {
  body?: StopDeviceRequestBody;
  pathParams: StopDevicePathParams;
} & ApiContext['fetcherOptions'];

export const fetchStopDevice = (
  variables: StopDeviceVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    undefined,
    StopDeviceError,
    StopDeviceRequestBody,
    {},
    {},
    StopDevicePathParams
  >({
    url: '/v1/devices/{deviceID}/state/stop',
    method: 'post',
    ...variables,
    signal,
  });

export const useStopDevice = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      StopDeviceError,
      StopDeviceVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    undefined,
    StopDeviceError,
    StopDeviceVariables
  >(
    (variables: StopDeviceVariables) =>
      fetchStopDevice({ ...fetcherOptions, ...variables }),
    options
  );
};

export type ResetDevicePathParams = {
  /**
   * The ID of the device to affect.
   */
  deviceID: Schemas.Uuid;
};

export type ResetDeviceError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: Responses.WrongStateError;
    }
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type ResetDeviceVariables = {
  body: Schemas.Reason;
  pathParams: ResetDevicePathParams;
} & ApiContext['fetcherOptions'];

export const fetchResetDevice = (
  variables: ResetDeviceVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    undefined,
    ResetDeviceError,
    Schemas.Reason,
    {},
    {},
    ResetDevicePathParams
  >({
    url: '/v1/devices/{deviceID}/state/reset',
    method: 'post',
    ...variables,
    signal,
  });

export const useResetDevice = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      ResetDeviceError,
      ResetDeviceVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    undefined,
    ResetDeviceError,
    ResetDeviceVariables
  >(
    (variables: ResetDeviceVariables) =>
      fetchResetDevice({ ...fetcherOptions, ...variables }),
    options
  );
};

export type RespondDeviceAlertPathParams = {
  /**
   * The ID of the device to affect.
   */
  deviceID: Schemas.Uuid;
};

export type RespondDeviceAlertError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: Responses.WrongStateError;
    }
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type RespondDeviceAlertVariables = {
  body: Schemas.AlertResponse;
  pathParams: RespondDeviceAlertPathParams;
} & ApiContext['fetcherOptions'];

export const fetchRespondDeviceAlert = (
  variables: RespondDeviceAlertVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    undefined,
    RespondDeviceAlertError,
    Schemas.AlertResponse,
    {},
    {},
    RespondDeviceAlertPathParams
  >({
    url: '/v1/devices/{deviceID}/state/alert-response',
    method: 'post',
    ...variables,
    signal,
  });

export const useRespondDeviceAlert = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      RespondDeviceAlertError,
      RespondDeviceAlertVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    undefined,
    RespondDeviceAlertError,
    RespondDeviceAlertVariables
  >(
    (variables: RespondDeviceAlertVariables) =>
      fetchRespondDeviceAlert({ ...fetcherOptions, ...variables }),
    options
  );
};

export type ActionDeviceErrorPathParams = {
  /**
   * The ID of the device to affect.
   */
  deviceID: Schemas.Uuid;
  /**
   * The ID of the device error to affect.
   */
  deviceErrorID: Schemas.Uuid;
};

export type ActionDeviceErrorError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type ActionDeviceErrorVariables = {
  body: Schemas.DeviceErrorActionPayload;
  pathParams: ActionDeviceErrorPathParams;
} & ApiContext['fetcherOptions'];

export const fetchActionDeviceError = (
  variables: ActionDeviceErrorVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    undefined,
    ActionDeviceErrorError,
    Schemas.DeviceErrorActionPayload,
    {},
    {},
    ActionDeviceErrorPathParams
  >({
    url: '/v1/devices/{deviceID}/errors/{deviceErrorID}/action',
    method: 'post',
    ...variables,
    signal,
  });

export const useActionDeviceError = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      ActionDeviceErrorError,
      ActionDeviceErrorVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    undefined,
    ActionDeviceErrorError,
    ActionDeviceErrorVariables
  >(
    (variables: ActionDeviceErrorVariables) =>
      fetchActionDeviceError({ ...fetcherOptions, ...variables }),
    options
  );
};

export type IgnoreTimeConstraintBrokenPromptPathParams = {
  /**
   * The ID of the device to affect.
   */
  deviceID: Schemas.Uuid;
  /**
   * The ID of the time constraint broken prompt to affect.
   */
  timeConstraintBrokenPromptID: Schemas.Uuid;
};

export type IgnoreTimeConstraintBrokenPromptError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type IgnoreTimeConstraintBrokenPromptVariables = {
  pathParams: IgnoreTimeConstraintBrokenPromptPathParams;
} & ApiContext['fetcherOptions'];

export const fetchIgnoreTimeConstraintBrokenPrompt = (
  variables: IgnoreTimeConstraintBrokenPromptVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    undefined,
    IgnoreTimeConstraintBrokenPromptError,
    undefined,
    {},
    {},
    IgnoreTimeConstraintBrokenPromptPathParams
  >({
    url: '/v1/devices/{deviceID}/time-constraints/{timeConstraintBrokenPromptID}/ignore',
    method: 'post',
    ...variables,
    signal,
  });

export const useIgnoreTimeConstraintBrokenPrompt = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      IgnoreTimeConstraintBrokenPromptError,
      IgnoreTimeConstraintBrokenPromptVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    undefined,
    IgnoreTimeConstraintBrokenPromptError,
    IgnoreTimeConstraintBrokenPromptVariables
  >(
    (variables: IgnoreTimeConstraintBrokenPromptVariables) =>
      fetchIgnoreTimeConstraintBrokenPrompt({
        ...fetcherOptions,
        ...variables,
      }),
    options
  );
};

export type UpdateGroupPathParams = {
  /**
   * The ID of the group to update.
   */
  groupID: Schemas.Uuid;
};

export type UpdateGroupError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: Responses.InvalidArguments;
    }
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type UpdateGroupVariables = {
  body: Schemas.Group;
  pathParams: UpdateGroupPathParams;
} & ApiContext['fetcherOptions'];

export const fetchUpdateGroup = (
  variables: UpdateGroupVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.Groups,
    UpdateGroupError,
    Schemas.Group,
    {},
    {},
    UpdateGroupPathParams
  >({
    url: '/v1/device-groups/{groupID}',
    method: 'put',
    ...variables,
    signal,
  });

export const useUpdateGroup = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Groups,
      UpdateGroupError,
      UpdateGroupVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    Schemas.Groups,
    UpdateGroupError,
    UpdateGroupVariables
  >(
    (variables: UpdateGroupVariables) =>
      fetchUpdateGroup({ ...fetcherOptions, ...variables }),
    options
  );
};

export type DeleteGroupPathParams = {
  /**
   * The ID of the group to delete.
   */
  groupID: Schemas.Uuid;
};

export type DeleteGroupError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type DeleteGroupVariables = {
  pathParams: DeleteGroupPathParams;
} & ApiContext['fetcherOptions'];

export const fetchDeleteGroup = (
  variables: DeleteGroupVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.Groups,
    DeleteGroupError,
    undefined,
    {},
    {},
    DeleteGroupPathParams
  >({
    url: '/v1/device-groups/{groupID}',
    method: 'delete',
    ...variables,
    signal,
  });

export const useDeleteGroup = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Groups,
      DeleteGroupError,
      DeleteGroupVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    Schemas.Groups,
    DeleteGroupError,
    DeleteGroupVariables
  >(
    (variables: DeleteGroupVariables) =>
      fetchDeleteGroup({ ...fetcherOptions, ...variables }),
    options
  );
};

export type UpdateDeviceGroupV2PathParams = {
  /**
   * The ID of the group to update.
   */
  groupID: Schemas.Uuid;
};

export type UpdateDeviceGroupV2Headers = {
  ['X-Request-Authorization']?: string;
};

export type UpdateDeviceGroupV2Error = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: Responses.InvalidArguments;
    }
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type UpdateDeviceGroupV2Variables = {
  body: Schemas.Group;
  headers?: UpdateDeviceGroupV2Headers;
  pathParams: UpdateDeviceGroupV2PathParams;
} & ApiContext['fetcherOptions'];

export const fetchUpdateDeviceGroupV2 = (
  variables: UpdateDeviceGroupV2Variables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.FlatGroup,
    UpdateDeviceGroupV2Error,
    Schemas.Group,
    UpdateDeviceGroupV2Headers,
    {},
    UpdateDeviceGroupV2PathParams
  >({
    url: '/v2/device-groups/{groupID}',
    method: 'put',
    ...variables,
    signal,
  });

export const useUpdateDeviceGroupV2 = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.FlatGroup,
      UpdateDeviceGroupV2Error,
      UpdateDeviceGroupV2Variables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    Schemas.FlatGroup,
    UpdateDeviceGroupV2Error,
    UpdateDeviceGroupV2Variables
  >(
    (variables: UpdateDeviceGroupV2Variables) =>
      fetchUpdateDeviceGroupV2({ ...fetcherOptions, ...variables }),
    options
  );
};

export type DeleteDeviceGroupV2PathParams = {
  /**
   * The ID of the group to delete.
   */
  groupID: Schemas.Uuid;
};

export type DeleteDeviceGroupV2Headers = {
  ['X-Request-Authorization']?: string;
};

export type DeleteDeviceGroupV2Error = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type DeleteDeviceGroupV2Variables = {
  headers?: DeleteDeviceGroupV2Headers;
  pathParams: DeleteDeviceGroupV2PathParams;
} & ApiContext['fetcherOptions'];

export const fetchDeleteDeviceGroupV2 = (
  variables: DeleteDeviceGroupV2Variables,
  signal?: AbortSignal
) =>
  apiFetch<
    undefined,
    DeleteDeviceGroupV2Error,
    undefined,
    DeleteDeviceGroupV2Headers,
    {},
    DeleteDeviceGroupV2PathParams
  >({
    url: '/v2/device-groups/{groupID}',
    method: 'delete',
    ...variables,
    signal,
  });

export const useDeleteDeviceGroupV2 = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      DeleteDeviceGroupV2Error,
      DeleteDeviceGroupV2Variables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    undefined,
    DeleteDeviceGroupV2Error,
    DeleteDeviceGroupV2Variables
  >(
    (variables: DeleteDeviceGroupV2Variables) =>
      fetchDeleteDeviceGroupV2({ ...fetcherOptions, ...variables }),
    options
  );
};

export type MoveDeviceBetweenGroupsPathParams = {
  /**
   * The ID of the device to affect.
   */
  deviceID: Schemas.Uuid;
};

export type MoveDeviceBetweenGroupsError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: Responses.InvalidArguments;
    }
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type MoveDeviceBetweenGroupsVariables = {
  body?: Schemas.MovePayload;
  pathParams: MoveDeviceBetweenGroupsPathParams;
} & ApiContext['fetcherOptions'];

export const fetchMoveDeviceBetweenGroups = (
  variables: MoveDeviceBetweenGroupsVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.Groups,
    MoveDeviceBetweenGroupsError,
    Schemas.MovePayload,
    {},
    {},
    MoveDeviceBetweenGroupsPathParams
  >({
    url: '/v1/devices/{deviceID}/groups/move',
    method: 'post',
    ...variables,
    signal,
  });

export const useMoveDeviceBetweenGroups = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Groups,
      MoveDeviceBetweenGroupsError,
      MoveDeviceBetweenGroupsVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    Schemas.Groups,
    MoveDeviceBetweenGroupsError,
    MoveDeviceBetweenGroupsVariables
  >(
    (variables: MoveDeviceBetweenGroupsVariables) =>
      fetchMoveDeviceBetweenGroups({ ...fetcherOptions, ...variables }),
    options
  );
};

export type MoveDeviceBetweenGroupsV2PathParams = {
  /**
   * The ID of the device to affect.
   */
  deviceID: Schemas.Uuid;
};

export type MoveDeviceBetweenGroupsV2Headers = {
  ['X-Request-Authorization']?: string;
};

export type MoveDeviceBetweenGroupsV2Error = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: Responses.InvalidArguments;
    }
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type MoveDeviceBetweenGroupsV2Variables = {
  body?: Schemas.MovePayload;
  headers?: MoveDeviceBetweenGroupsV2Headers;
  pathParams: MoveDeviceBetweenGroupsV2PathParams;
} & ApiContext['fetcherOptions'];

export const fetchMoveDeviceBetweenGroupsV2 = (
  variables: MoveDeviceBetweenGroupsV2Variables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.MoveResult,
    MoveDeviceBetweenGroupsV2Error,
    Schemas.MovePayload,
    MoveDeviceBetweenGroupsV2Headers,
    {},
    MoveDeviceBetweenGroupsV2PathParams
  >({
    url: '/v2/devices/{deviceID}/groups/move',
    method: 'post',
    ...variables,
    signal,
  });

export const useMoveDeviceBetweenGroupsV2 = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.MoveResult,
      MoveDeviceBetweenGroupsV2Error,
      MoveDeviceBetweenGroupsV2Variables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    Schemas.MoveResult,
    MoveDeviceBetweenGroupsV2Error,
    MoveDeviceBetweenGroupsV2Variables
  >(
    (variables: MoveDeviceBetweenGroupsV2Variables) =>
      fetchMoveDeviceBetweenGroupsV2({ ...fetcherOptions, ...variables }),
    options
  );
};

export type GetWorkspaceWorkflowsPathParams = {
  /**
   * The ID of the workspace to use.
   */
  workspaceID: Schemas.Uuid;
};

export type GetWorkspaceWorkflowsQueryParams = {
  /**
   * The cursor from which to start the listing, leave empty for the first request.
   * A cursor is only valid if you use the same options/arguments for each request.
   */
  cursor?: string;
  /**
   * what is the date & time of the first event to return, defaults to `7 days ago`
   *
   * @format date-time
   */
  range_start?: string;
  /**
   * what is the date & time of the last event to return, defaults to `now`
   *
   * @format date-time
   */
  range_end?: string;
  /**
   * How many results to return? Defaults to `100`
   *
   * @minimum 10
   * @maximum 1000
   */
  count?: number;
  /**
   * What property to use to order the entries? Defaults to `name`
   */
  order_by?: Schemas.PaginationOrderBy;
  /**
   * What order should the entries have? Defaults to `ascending`
   */
  order?: Schemas.PaginationOrder;
  /**
   * Regex filter (RE2 syntax) for the name of the items, defaults to empty, which returns everything
   */
  filter?: string;
  /**
   * The tag to filter by.
   */
  tags?: Schemas.Uuid[];
};

export type GetWorkspaceWorkflowsHeaders = {
  ['X-Request-Authorization']?: string;
};

export type GetWorkspaceWorkflowsError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type GetWorkspaceWorkflowsVariables = {
  headers?: GetWorkspaceWorkflowsHeaders;
  pathParams: GetWorkspaceWorkflowsPathParams;
  queryParams?: GetWorkspaceWorkflowsQueryParams;
} & ApiContext['fetcherOptions'];

export const fetchGetWorkspaceWorkflows = (
  variables: GetWorkspaceWorkflowsVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.Workflows,
    GetWorkspaceWorkflowsError,
    undefined,
    GetWorkspaceWorkflowsHeaders,
    GetWorkspaceWorkflowsQueryParams,
    GetWorkspaceWorkflowsPathParams
  >({
    url: '/v1/workspace/{workspaceID}/workflows',
    method: 'get',
    ...variables,
    signal,
  });

export const useGetWorkspaceWorkflows = <TData = Schemas.Workflows>(
  variables: GetWorkspaceWorkflowsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      Schemas.Workflows,
      GetWorkspaceWorkflowsError,
      TData
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useApiContext(options);
  return reactQuery.useQuery<
    Schemas.Workflows,
    GetWorkspaceWorkflowsError,
    TData
  >(
    queryKeyFn({
      path: '/v1/workspace/{workspaceID}/workflows',
      operationId: 'getWorkspaceWorkflows',
      variables,
    }),
    ({ signal }) =>
      fetchGetWorkspaceWorkflows({ ...fetcherOptions, ...variables }, signal),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type CreateWorkflowPathParams = {
  /**
   * The ID of the workspace to use.
   */
  workspaceID: Schemas.Uuid;
};

export type CreateWorkflowError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: Responses.InvalidArguments;
    }
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type CreateWorkflowVariables = {
  body: Schemas.WorkflowCreatePayload;
  pathParams: CreateWorkflowPathParams;
} & ApiContext['fetcherOptions'];

export const fetchCreateWorkflow = (
  variables: CreateWorkflowVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.Workflow,
    CreateWorkflowError,
    Schemas.WorkflowCreatePayload,
    {},
    {},
    CreateWorkflowPathParams
  >({
    url: '/v1/workspace/{workspaceID}/workflows',
    method: 'post',
    ...variables,
    signal,
  });

export const useCreateWorkflow = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Workflow,
      CreateWorkflowError,
      CreateWorkflowVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    Schemas.Workflow,
    CreateWorkflowError,
    CreateWorkflowVariables
  >(
    (variables: CreateWorkflowVariables) =>
      fetchCreateWorkflow({ ...fetcherOptions, ...variables }),
    options
  );
};

export type GetWorkspaceDevicesPathParams = {
  /**
   * The ID of the workspace to use.
   */
  workspaceID: Schemas.Uuid;
};

export type GetWorkspaceDevicesQueryParams = {
  /**
   * The cursor from which to start the listing, leave empty for the first request.
   * A cursor is only valid if you use the same options/arguments for each request.
   */
  cursor?: string;
  /**
   * How many results to return? Defaults to `100`
   *
   * @minimum 10
   * @maximum 1000
   */
  count?: number;
  /**
   * What property to use to order the entries? Defaults to `name`
   */
  order_by?: Schemas.PaginationOrderBy;
  /**
   * What order should the entries have? Defaults to `ascending`
   */
  order?: Schemas.PaginationOrder;
  /**
   * Regex filter (RE2 syntax) for the name of the items, defaults to empty, which returns everything
   */
  filter?: string;
};

export type GetWorkspaceDevicesError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type GetWorkspaceDevicesVariables = {
  pathParams: GetWorkspaceDevicesPathParams;
  queryParams?: GetWorkspaceDevicesQueryParams;
} & ApiContext['fetcherOptions'];

export const fetchGetWorkspaceDevices = (
  variables: GetWorkspaceDevicesVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.Devices,
    GetWorkspaceDevicesError,
    undefined,
    {},
    GetWorkspaceDevicesQueryParams,
    GetWorkspaceDevicesPathParams
  >({
    url: '/v1/workspace/{workspaceID}/devices',
    method: 'get',
    ...variables,
    signal,
  });

export const useGetWorkspaceDevices = <TData = Schemas.Devices>(
  variables: GetWorkspaceDevicesVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      Schemas.Devices,
      GetWorkspaceDevicesError,
      TData
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useApiContext(options);
  return reactQuery.useQuery<Schemas.Devices, GetWorkspaceDevicesError, TData>(
    queryKeyFn({
      path: '/v1/workspace/{workspaceID}/devices',
      operationId: 'getWorkspaceDevices',
      variables,
    }),
    ({ signal }) =>
      fetchGetWorkspaceDevices({ ...fetcherOptions, ...variables }, signal),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type GetWorkspaceGroupsPathParams = {
  /**
   * The ID of the workspace to use.
   */
  workspaceID: Schemas.Uuid;
};

export type GetWorkspaceGroupsError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type GetWorkspaceGroupsVariables = {
  pathParams: GetWorkspaceGroupsPathParams;
} & ApiContext['fetcherOptions'];

export const fetchGetWorkspaceGroups = (
  variables: GetWorkspaceGroupsVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.Groups,
    GetWorkspaceGroupsError,
    undefined,
    {},
    {},
    GetWorkspaceGroupsPathParams
  >({
    url: '/v1/workspace/{workspaceID}/device-groups',
    method: 'get',
    ...variables,
    signal,
  });

export const useGetWorkspaceGroups = <TData = Schemas.Groups>(
  variables: GetWorkspaceGroupsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Groups, GetWorkspaceGroupsError, TData>,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useApiContext(options);
  return reactQuery.useQuery<Schemas.Groups, GetWorkspaceGroupsError, TData>(
    queryKeyFn({
      path: '/v1/workspace/{workspaceID}/device-groups',
      operationId: 'getWorkspaceGroups',
      variables,
    }),
    ({ signal }) =>
      fetchGetWorkspaceGroups({ ...fetcherOptions, ...variables }, signal),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type CreateGroupPathParams = {
  /**
   * The ID of the workspace to use.
   */
  workspaceID: Schemas.Uuid;
};

export type CreateGroupError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: Responses.InvalidArguments;
    }
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type CreateGroupVariables = {
  body: Schemas.Group;
  pathParams: CreateGroupPathParams;
} & ApiContext['fetcherOptions'];

export const fetchCreateGroup = (
  variables: CreateGroupVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.GroupCreateResult,
    CreateGroupError,
    Schemas.Group,
    {},
    {},
    CreateGroupPathParams
  >({
    url: '/v1/workspace/{workspaceID}/device-groups',
    method: 'post',
    ...variables,
    signal,
  });

export const useCreateGroup = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.GroupCreateResult,
      CreateGroupError,
      CreateGroupVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    Schemas.GroupCreateResult,
    CreateGroupError,
    CreateGroupVariables
  >(
    (variables: CreateGroupVariables) =>
      fetchCreateGroup({ ...fetcherOptions, ...variables }),
    options
  );
};

export type GetWorkspaceDeviceGroupsV2PathParams = {
  /**
   * The ID of the workspace to use.
   */
  workspaceID: Schemas.Uuid;
};

export type GetWorkspaceDeviceGroupsV2QueryParams = {
  /**
   * The cursor from which to start the listing, leave empty for the first request.
   * A cursor is only valid if you use the same options/arguments for each request.
   */
  cursor?: string;
  /**
   * How many results to return? Defaults to `100`
   *
   * @minimum 10
   * @maximum 1000
   */
  count?: number;
  /**
   * What property to use to order the entries? Defaults to `name`
   */
  order_by?: Schemas.PaginationOrderBy;
  /**
   * What order should the entries have? Defaults to `ascending`
   */
  order?: Schemas.PaginationOrder;
  /**
   * Regex filter (RE2 syntax) for the name of the items, defaults to empty, which returns everything
   */
  filter?: string;
};

export type GetWorkspaceDeviceGroupsV2Error = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type GetWorkspaceDeviceGroupsV2Variables = {
  pathParams: GetWorkspaceDeviceGroupsV2PathParams;
  queryParams?: GetWorkspaceDeviceGroupsV2QueryParams;
} & ApiContext['fetcherOptions'];

export const fetchGetWorkspaceDeviceGroupsV2 = (
  variables: GetWorkspaceDeviceGroupsV2Variables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.PaginatedFlatGroups,
    GetWorkspaceDeviceGroupsV2Error,
    undefined,
    {},
    GetWorkspaceDeviceGroupsV2QueryParams,
    GetWorkspaceDeviceGroupsV2PathParams
  >({
    url: '/v2/workspace/{workspaceID}/device-groups',
    method: 'get',
    ...variables,
    signal,
  });

export const useGetWorkspaceDeviceGroupsV2 = <
  TData = Schemas.PaginatedFlatGroups
>(
  variables: GetWorkspaceDeviceGroupsV2Variables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      Schemas.PaginatedFlatGroups,
      GetWorkspaceDeviceGroupsV2Error,
      TData
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useApiContext(options);
  return reactQuery.useQuery<
    Schemas.PaginatedFlatGroups,
    GetWorkspaceDeviceGroupsV2Error,
    TData
  >(
    queryKeyFn({
      path: '/v2/workspace/{workspaceID}/device-groups',
      operationId: 'getWorkspaceDeviceGroupsV2',
      variables,
    }),
    ({ signal }) =>
      fetchGetWorkspaceDeviceGroupsV2(
        { ...fetcherOptions, ...variables },
        signal
      ),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type CreateDeviceGroupV2PathParams = {
  /**
   * The ID of the workspace to use.
   */
  workspaceID: Schemas.Uuid;
};

export type CreateDeviceGroupV2Headers = {
  ['X-Request-Authorization']?: string;
};

export type CreateDeviceGroupV2Error = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: Responses.InvalidArguments;
    }
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type CreateDeviceGroupV2Variables = {
  body: Schemas.Group;
  headers?: CreateDeviceGroupV2Headers;
  pathParams: CreateDeviceGroupV2PathParams;
} & ApiContext['fetcherOptions'];

export const fetchCreateDeviceGroupV2 = (
  variables: CreateDeviceGroupV2Variables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.FlatGroup,
    CreateDeviceGroupV2Error,
    Schemas.Group,
    CreateDeviceGroupV2Headers,
    {},
    CreateDeviceGroupV2PathParams
  >({
    url: '/v2/workspace/{workspaceID}/device-groups',
    method: 'post',
    ...variables,
    signal,
  });

export const useCreateDeviceGroupV2 = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.FlatGroup,
      CreateDeviceGroupV2Error,
      CreateDeviceGroupV2Variables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    Schemas.FlatGroup,
    CreateDeviceGroupV2Error,
    CreateDeviceGroupV2Variables
  >(
    (variables: CreateDeviceGroupV2Variables) =>
      fetchCreateDeviceGroupV2({ ...fetcherOptions, ...variables }),
    options
  );
};

export type GetUserOrganizationsQueryParams = {
  /**
   * The cursor from which to start the listing, leave empty for the first request.
   * A cursor is only valid if you use the same options/arguments for each request.
   */
  cursor?: string;
  /**
   * How many results to return? Defaults to `100`
   *
   * @minimum 10
   * @maximum 1000
   */
  count?: number;
  /**
   * What order should the entries have? Defaults to `ascending`
   */
  order?: Schemas.PaginationOrder;
};

export type GetUserOrganizationsError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type GetUserOrganizationsVariables = {
  queryParams?: GetUserOrganizationsQueryParams;
} & ApiContext['fetcherOptions'];

export const fetchGetUserOrganizations = (
  variables: GetUserOrganizationsVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.Organizations,
    GetUserOrganizationsError,
    undefined,
    {},
    GetUserOrganizationsQueryParams,
    {}
  >({ url: '/v1/user/organizations', method: 'get', ...variables, signal });

export const useGetUserOrganizations = <TData = Schemas.Organizations>(
  variables: GetUserOrganizationsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      Schemas.Organizations,
      GetUserOrganizationsError,
      TData
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useApiContext(options);
  return reactQuery.useQuery<
    Schemas.Organizations,
    GetUserOrganizationsError,
    TData
  >(
    queryKeyFn({
      path: '/v1/user/organizations',
      operationId: 'getUserOrganizations',
      variables,
    }),
    ({ signal }) =>
      fetchGetUserOrganizations({ ...fetcherOptions, ...variables }, signal),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type GetUserOrganizationsV2Error = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type GetUserOrganizationsV2Variables = ApiContext['fetcherOptions'];

export const fetchGetUserOrganizationsV2 = (
  variables: GetUserOrganizationsV2Variables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.OrganizationsV2,
    GetUserOrganizationsV2Error,
    undefined,
    {},
    {},
    {}
  >({ url: '/v2/user/organizations', method: 'get', ...variables, signal });

export const useGetUserOrganizationsV2 = <TData = Schemas.OrganizationsV2>(
  variables: GetUserOrganizationsV2Variables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      Schemas.OrganizationsV2,
      GetUserOrganizationsV2Error,
      TData
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useApiContext(options);
  return reactQuery.useQuery<
    Schemas.OrganizationsV2,
    GetUserOrganizationsV2Error,
    TData
  >(
    queryKeyFn({
      path: '/v2/user/organizations',
      operationId: 'getUserOrganizationsV2',
      variables,
    }),
    ({ signal }) =>
      fetchGetUserOrganizationsV2({ ...fetcherOptions, ...variables }, signal),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type GetUserError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type GetUserVariables = ApiContext['fetcherOptions'];

export const fetchGetUser = (
  variables: GetUserVariables,
  signal?: AbortSignal
) =>
  apiFetch<Schemas.User, GetUserError, undefined, {}, {}, {}>({
    url: '/v1/user',
    method: 'get',
    ...variables,
    signal,
  });

export const useGetUser = <TData = Schemas.User>(
  variables: GetUserVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.User, GetUserError, TData>,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useApiContext(options);
  return reactQuery.useQuery<Schemas.User, GetUserError, TData>(
    queryKeyFn({ path: '/v1/user', operationId: 'getUser', variables }),
    ({ signal }) => fetchGetUser({ ...fetcherOptions, ...variables }, signal),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type RequestIntentionError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type RequestIntentionVariables = {
  body: Schemas.RequestIntentionRequest;
} & ApiContext['fetcherOptions'];

export const fetchRequestIntention = (
  variables: RequestIntentionVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.RequestIntentionResponse,
    RequestIntentionError,
    Schemas.RequestIntentionRequest,
    {},
    {},
    {}
  >({ url: '/v1/request/intention', method: 'post', ...variables, signal });

export const useRequestIntention = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.RequestIntentionResponse,
      RequestIntentionError,
      RequestIntentionVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    Schemas.RequestIntentionResponse,
    RequestIntentionError,
    RequestIntentionVariables
  >(
    (variables: RequestIntentionVariables) =>
      fetchRequestIntention({ ...fetcherOptions, ...variables }),
    options
  );
};

export type RequestVerifyHeaders = {
  ['X-Id-Token']: string;
};

export type RequestVerifyError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type RequestVerifyVariables = {
  body: Schemas.RequestVerifyRequest;
  headers: RequestVerifyHeaders;
} & ApiContext['fetcherOptions'];

export const fetchRequestVerify = (
  variables: RequestVerifyVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.RequestVerifyResponse,
    RequestVerifyError,
    Schemas.RequestVerifyRequest,
    RequestVerifyHeaders,
    {},
    {}
  >({ url: '/v1/request/verify', method: 'post', ...variables, signal });

export const useRequestVerify = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.RequestVerifyResponse,
      RequestVerifyError,
      RequestVerifyVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    Schemas.RequestVerifyResponse,
    RequestVerifyError,
    RequestVerifyVariables
  >(
    (variables: RequestVerifyVariables) =>
      fetchRequestVerify({ ...fetcherOptions, ...variables }),
    options
  );
};

export type DummyInternalDoNotUseError = Fetcher.ErrorWrapper<{
  status: 500;
  payload: Responses.InternalError;
}>;

export type DummyInternalDoNotUseVariables = {
  body?:
    | Schemas.DeviceDataSubscription
    | Schemas.DeviceDataSubMetric
    | Schemas.DeviceDataSubLog;
} & ApiContext['fetcherOptions'];

/**
 * This endpoint is provided only to ensure every type for the Socket.IO endpoint are used
 * so Swagger doesn't raise an error and code generators don't ignore the unused types.
 */
export const fetchDummyInternalDoNotUse = (
  variables: DummyInternalDoNotUseVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    undefined,
    DummyInternalDoNotUseError,
    | Schemas.DeviceDataSubscription
    | Schemas.DeviceDataSubMetric
    | Schemas.DeviceDataSubLog,
    {},
    {},
    {}
  >({ url: '/v1/stream', method: 'post', ...variables, signal });

/**
 * This endpoint is provided only to ensure every type for the Socket.IO endpoint are used
 * so Swagger doesn't raise an error and code generators don't ignore the unused types.
 */
export const useDummyInternalDoNotUse = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      DummyInternalDoNotUseError,
      DummyInternalDoNotUseVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    undefined,
    DummyInternalDoNotUseError,
    DummyInternalDoNotUseVariables
  >(
    (variables: DummyInternalDoNotUseVariables) =>
      fetchDummyInternalDoNotUse({ ...fetcherOptions, ...variables }),
    options
  );
};

export type WsStreamError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type WsStreamVariables = ApiContext['fetcherOptions'];

/**
 * As Swagger doesn't support Websocket or Socket.IO, the protocol will be described textually in this section
 *
 * # Security & Authentication
 * You must either use JWT or API key authentication to use this endpoint.
 * This is given to the Socket.IO client as a `auth` object, which follows the following schema:
 * ```
 * {
 *   "mode": "JWT" | "API",
 *   "token": <either JWT token or API token depending on the mode>,
 * }
 * ```
 *
 * # Events
 * Some events will be broadcasted to you even without subscribing to them.
 *
 * ## Versions
 *
 * *Namespace*: `versions`
 *
 * ### Creation
 *
 * *Event*: `created`
 *
 * *Trigger*: when a new version is created
 *
 * *Content*: `$ref: '#/components/schemas/DeviceVersion'`
 *
 * ## Workflow lifecycle
 *
 * *Namespace*: `workflows`
 *
 * ### Creation
 *
 * *Event*: `created`
 *
 * *Trigger*: when a workflow is created
 *
 * *Content*: `$ref: '#/components/schemas/Workflow'`
 *
 * ### Updated
 *
 * *Event*: `updated`
 *
 * *Trigger*: when a workflow is updated
 *
 * *Content*: `$ref: '#/components/schemas/Workflow'`
 *
 * ### Deleted
 *
 * *Event*: `deleted`
 *
 * *Trigger*: when a workflow is deleted
 *
 * *Content*: `$ref: '#/components/schemas/Workflow'`
 *
 * ## Run history lifecycle
 *
 * *Namespace*: `run-histories`
 *
 * ### Creation
 *
 * *Event*: `created`
 *
 * *Trigger*: when a run-history is created
 *
 * *Content*: `$ref: '#/components/schemas/RunHistory'`
 *
 * ### Updated
 *
 * *Event*: `updated`
 *
 * *Trigger*: when a run-history is updated
 *
 * *Content*: `$ref: '#/components/schemas/RunHistory'`
 *
 * ## Device lifecycle
 *
 * *Namespace*: `devices`
 *
 * ### Association
 *
 * *Event*: `associated`
 *
 * *Trigger*: when a device is associated with the account
 *
 * *Content*: `$ref: '#/components/schemas/Device'`
 *
 * ### Status change
 *
 * *Event*: `status`
 *
 * *Trigger*: when a device status changes
 *
 * *Content*: `$ref: '#/components/schemas/Device'`
 *
 * ### Configuration change
 *
 * *Event*: `configuration`
 *
 * *Trigger*: when a device configuration changes
 *
 * *Content*: `$ref: '#/components/schemas/DeviceConfiguration'`
 *
 * ### Groups changed
 *
 * *Event*: `groups`
 *
 * *Trigger*: when groups changed
 *
 * *Content*: `$ref: '#/components/schemas/Groups'`
 *
 * ### (V2) Group Updated
 * *Event*: `updated`
 *
 * *Trigger*: when groups changed
 *
 * *Content*: `$ref: '#/components/schemas/FlatGroup'`
 *
 *
 * ### (V2) Group Created
 * *Event*: `created`
 *
 * *Trigger*: when group created
 *
 * *Content*: `$ref: '#/components/schemas/FlatGroup'`
 *
 *
 * ### (V2) Group Deleted
 * *Event*: `deleted`
 *
 * *Trigger*: Deleted group
 *
 * *Content*: `$ref: '#/components/schemas/FlatGroup'`
 *
 *
 * ### Device data
 *
 * This event uses a subscription-system to save bandwidth,
 * if you want to receive this data, you will need to send some of these messages.
 *
 * ### Subscribe
 *
 * *Event*: `data/subscribe`
 *
 * *Trigger*: client-side, when you want to listen to a particular device
 *
 * *Content*: `$ref: '#/components/schemas/DeviceDataSubscription'`
 *
 * *Response*: `nil` (no error) or `$ref: '#/components/schemas/Error'`
 *
 * ### Unsubscribe
 *
 * *Event*: `data/unsubscribe`
 *
 * *Trigger*: client-side, when you don't want to listen to a particular device anymore
 *
 * *Content*: `$ref: '#/components/schemas/DeviceDataSubscription'`
 *
 * *Response*: `nil` (no error) or `$ref: '#/components/schemas/Error'`
 *
 * ### New device metric
 *
 * *Event*: `metrics`
 *
 * *Trigger*: when a new metric is received
 *
 * *Content*: `$ref: '#/components/schemas/DeviceDataSubMetric'`
 *
 * ### New device log line
 *
 * *Event*: `logs`
 *
 * *Trigger*: when a new log line is received
 *
 * *Content*: `$ref: '#/components/schemas/DeviceDataSubLog'`
 *
 * ### Device error
 *
 * *Event*: `device_error`
 *
 * *Trigger*: when a device error is received
 *
 * *Content*: `$ref: '#/components/schemas/DeviceError'`
 */
export const fetchWsStream = (
  variables: WsStreamVariables,
  signal?: AbortSignal
) =>
  apiFetch<undefined, WsStreamError, undefined, {}, {}, {}>({
    url: '/v1/stream',
    method: 'get',
    ...variables,
    signal,
  });

/**
 * As Swagger doesn't support Websocket or Socket.IO, the protocol will be described textually in this section
 *
 * # Security & Authentication
 * You must either use JWT or API key authentication to use this endpoint.
 * This is given to the Socket.IO client as a `auth` object, which follows the following schema:
 * ```
 * {
 *   "mode": "JWT" | "API",
 *   "token": <either JWT token or API token depending on the mode>,
 * }
 * ```
 *
 * # Events
 * Some events will be broadcasted to you even without subscribing to them.
 *
 * ## Versions
 *
 * *Namespace*: `versions`
 *
 * ### Creation
 *
 * *Event*: `created`
 *
 * *Trigger*: when a new version is created
 *
 * *Content*: `$ref: '#/components/schemas/DeviceVersion'`
 *
 * ## Workflow lifecycle
 *
 * *Namespace*: `workflows`
 *
 * ### Creation
 *
 * *Event*: `created`
 *
 * *Trigger*: when a workflow is created
 *
 * *Content*: `$ref: '#/components/schemas/Workflow'`
 *
 * ### Updated
 *
 * *Event*: `updated`
 *
 * *Trigger*: when a workflow is updated
 *
 * *Content*: `$ref: '#/components/schemas/Workflow'`
 *
 * ### Deleted
 *
 * *Event*: `deleted`
 *
 * *Trigger*: when a workflow is deleted
 *
 * *Content*: `$ref: '#/components/schemas/Workflow'`
 *
 * ## Run history lifecycle
 *
 * *Namespace*: `run-histories`
 *
 * ### Creation
 *
 * *Event*: `created`
 *
 * *Trigger*: when a run-history is created
 *
 * *Content*: `$ref: '#/components/schemas/RunHistory'`
 *
 * ### Updated
 *
 * *Event*: `updated`
 *
 * *Trigger*: when a run-history is updated
 *
 * *Content*: `$ref: '#/components/schemas/RunHistory'`
 *
 * ## Device lifecycle
 *
 * *Namespace*: `devices`
 *
 * ### Association
 *
 * *Event*: `associated`
 *
 * *Trigger*: when a device is associated with the account
 *
 * *Content*: `$ref: '#/components/schemas/Device'`
 *
 * ### Status change
 *
 * *Event*: `status`
 *
 * *Trigger*: when a device status changes
 *
 * *Content*: `$ref: '#/components/schemas/Device'`
 *
 * ### Configuration change
 *
 * *Event*: `configuration`
 *
 * *Trigger*: when a device configuration changes
 *
 * *Content*: `$ref: '#/components/schemas/DeviceConfiguration'`
 *
 * ### Groups changed
 *
 * *Event*: `groups`
 *
 * *Trigger*: when groups changed
 *
 * *Content*: `$ref: '#/components/schemas/Groups'`
 *
 * ### (V2) Group Updated
 * *Event*: `updated`
 *
 * *Trigger*: when groups changed
 *
 * *Content*: `$ref: '#/components/schemas/FlatGroup'`
 *
 *
 * ### (V2) Group Created
 * *Event*: `created`
 *
 * *Trigger*: when group created
 *
 * *Content*: `$ref: '#/components/schemas/FlatGroup'`
 *
 *
 * ### (V2) Group Deleted
 * *Event*: `deleted`
 *
 * *Trigger*: Deleted group
 *
 * *Content*: `$ref: '#/components/schemas/FlatGroup'`
 *
 *
 * ### Device data
 *
 * This event uses a subscription-system to save bandwidth,
 * if you want to receive this data, you will need to send some of these messages.
 *
 * ### Subscribe
 *
 * *Event*: `data/subscribe`
 *
 * *Trigger*: client-side, when you want to listen to a particular device
 *
 * *Content*: `$ref: '#/components/schemas/DeviceDataSubscription'`
 *
 * *Response*: `nil` (no error) or `$ref: '#/components/schemas/Error'`
 *
 * ### Unsubscribe
 *
 * *Event*: `data/unsubscribe`
 *
 * *Trigger*: client-side, when you don't want to listen to a particular device anymore
 *
 * *Content*: `$ref: '#/components/schemas/DeviceDataSubscription'`
 *
 * *Response*: `nil` (no error) or `$ref: '#/components/schemas/Error'`
 *
 * ### New device metric
 *
 * *Event*: `metrics`
 *
 * *Trigger*: when a new metric is received
 *
 * *Content*: `$ref: '#/components/schemas/DeviceDataSubMetric'`
 *
 * ### New device log line
 *
 * *Event*: `logs`
 *
 * *Trigger*: when a new log line is received
 *
 * *Content*: `$ref: '#/components/schemas/DeviceDataSubLog'`
 *
 * ### Device error
 *
 * *Event*: `device_error`
 *
 * *Trigger*: when a device error is received
 *
 * *Content*: `$ref: '#/components/schemas/DeviceError'`
 */
export const useWsStream = <TData = undefined>(
  variables: WsStreamVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<undefined, WsStreamError, TData>,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useApiContext(options);
  return reactQuery.useQuery<undefined, WsStreamError, TData>(
    queryKeyFn({ path: '/v1/stream', operationId: 'wsStream', variables }),
    ({ signal }) => fetchWsStream({ ...fetcherOptions, ...variables }, signal),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type PutUserNotificationsPreferencesPathParams = {
  /**
   * The ID of the user to affect.
   */
  userID: string;
};

export type PutUserNotificationsPreferencesError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type PutUserNotificationsPreferencesVariables = {
  body: Schemas.UserNotificationPreferences;
  pathParams: PutUserNotificationsPreferencesPathParams;
} & ApiContext['fetcherOptions'];

export const fetchPutUserNotificationsPreferences = (
  variables: PutUserNotificationsPreferencesVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.UserNotificationPreferences,
    PutUserNotificationsPreferencesError,
    Schemas.UserNotificationPreferences,
    {},
    {},
    PutUserNotificationsPreferencesPathParams
  >({
    url: '/v1/users/{userID}/notifications/preferences',
    method: 'put',
    ...variables,
    signal,
  });

export const usePutUserNotificationsPreferences = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.UserNotificationPreferences,
      PutUserNotificationsPreferencesError,
      PutUserNotificationsPreferencesVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    Schemas.UserNotificationPreferences,
    PutUserNotificationsPreferencesError,
    PutUserNotificationsPreferencesVariables
  >(
    (variables: PutUserNotificationsPreferencesVariables) =>
      fetchPutUserNotificationsPreferences({ ...fetcherOptions, ...variables }),
    options
  );
};

export type GetUserNotificationsPreferencesPathParams = {
  /**
   * The ID of the user to affect.
   */
  userID: string;
};

export type GetUserNotificationsPreferencesError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type GetUserNotificationsPreferencesVariables = {
  pathParams: GetUserNotificationsPreferencesPathParams;
} & ApiContext['fetcherOptions'];

export const fetchGetUserNotificationsPreferences = (
  variables: GetUserNotificationsPreferencesVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.UserNotificationPreferences,
    GetUserNotificationsPreferencesError,
    undefined,
    {},
    {},
    GetUserNotificationsPreferencesPathParams
  >({
    url: '/v1/users/{userID}/notifications/preferences',
    method: 'get',
    ...variables,
    signal,
  });

export const useGetUserNotificationsPreferences = <
  TData = Schemas.UserNotificationPreferences
>(
  variables: GetUserNotificationsPreferencesVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      Schemas.UserNotificationPreferences,
      GetUserNotificationsPreferencesError,
      TData
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useApiContext(options);
  return reactQuery.useQuery<
    Schemas.UserNotificationPreferences,
    GetUserNotificationsPreferencesError,
    TData
  >(
    queryKeyFn({
      path: '/v1/users/{userID}/notifications/preferences',
      operationId: 'getUserNotificationsPreferences',
      variables,
    }),
    ({ signal }) =>
      fetchGetUserNotificationsPreferences(
        { ...fetcherOptions, ...variables },
        signal
      ),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type PostDeviceNotificationPreferencesPathParams = {
  /**
   * The ID of the user to affect.
   */
  userID: string;
  /**
   * The ID of the device to affect.
   */
  deviceID: Schemas.Uuid;
};

export type PostDeviceNotificationPreferencesError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type PostDeviceNotificationPreferencesVariables = {
  body: Schemas.DeviceNotificationPreferences;
  pathParams: PostDeviceNotificationPreferencesPathParams;
} & ApiContext['fetcherOptions'];

export const fetchPostDeviceNotificationPreferences = (
  variables: PostDeviceNotificationPreferencesVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    undefined,
    PostDeviceNotificationPreferencesError,
    Schemas.DeviceNotificationPreferences,
    {},
    {},
    PostDeviceNotificationPreferencesPathParams
  >({
    url: '/v1/users/{userID}/notifications/devices/{deviceID}/preferences',
    method: 'put',
    ...variables,
    signal,
  });

export const usePostDeviceNotificationPreferences = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      PostDeviceNotificationPreferencesError,
      PostDeviceNotificationPreferencesVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    undefined,
    PostDeviceNotificationPreferencesError,
    PostDeviceNotificationPreferencesVariables
  >(
    (variables: PostDeviceNotificationPreferencesVariables) =>
      fetchPostDeviceNotificationPreferences({
        ...fetcherOptions,
        ...variables,
      }),
    options
  );
};

export type GetOrganizationsError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type GetOrganizationsVariables = ApiContext['fetcherOptions'];

export const fetchGetOrganizations = (
  variables: GetOrganizationsVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.OrganizationsV2,
    GetOrganizationsError,
    undefined,
    {},
    {},
    {}
  >({ url: '/v1/organizations', method: 'get', ...variables, signal });

export const useGetOrganizations = <TData = Schemas.OrganizationsV2>(
  variables: GetOrganizationsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      Schemas.OrganizationsV2,
      GetOrganizationsError,
      TData
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useApiContext(options);
  return reactQuery.useQuery<
    Schemas.OrganizationsV2,
    GetOrganizationsError,
    TData
  >(
    queryKeyFn({
      path: '/v1/organizations',
      operationId: 'getOrganizations',
      variables,
    }),
    ({ signal }) =>
      fetchGetOrganizations({ ...fetcherOptions, ...variables }, signal),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type CreateOrganizationError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: Responses.InvalidArguments;
    }
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type CreateOrganizationVariables = {
  body: Schemas.OrganizationPayload;
} & ApiContext['fetcherOptions'];

export const fetchCreateOrganization = (
  variables: CreateOrganizationVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.Organization,
    CreateOrganizationError,
    Schemas.OrganizationPayload,
    {},
    {},
    {}
  >({ url: '/v1/organizations', method: 'post', ...variables, signal });

export const useCreateOrganization = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Organization,
      CreateOrganizationError,
      CreateOrganizationVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    Schemas.Organization,
    CreateOrganizationError,
    CreateOrganizationVariables
  >(
    (variables: CreateOrganizationVariables) =>
      fetchCreateOrganization({ ...fetcherOptions, ...variables }),
    options
  );
};

export type GetOrganizationPathParams = {
  /**
   * The ID of the organization to query.
   */
  organizationID: Schemas.Uuid;
};

export type GetOrganizationError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type GetOrganizationVariables = {
  pathParams: GetOrganizationPathParams;
} & ApiContext['fetcherOptions'];

export const fetchGetOrganization = (
  variables: GetOrganizationVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.Organization,
    GetOrganizationError,
    undefined,
    {},
    {},
    GetOrganizationPathParams
  >({
    url: '/v1/organizations/{organizationID}',
    method: 'get',
    ...variables,
    signal,
  });

export const useGetOrganization = <TData = Schemas.Organization>(
  variables: GetOrganizationVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      Schemas.Organization,
      GetOrganizationError,
      TData
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useApiContext(options);
  return reactQuery.useQuery<Schemas.Organization, GetOrganizationError, TData>(
    queryKeyFn({
      path: '/v1/organizations/{organizationID}',
      operationId: 'getOrganization',
      variables,
    }),
    ({ signal }) =>
      fetchGetOrganization({ ...fetcherOptions, ...variables }, signal),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type UpdateOrganizationPathParams = {
  /**
   * The ID of the organization to query.
   */
  organizationID: Schemas.Uuid;
};

export type UpdateOrganizationHeaders = {
  ['X-Request-Authorization']?: string;
};

export type UpdateOrganizationError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: Responses.InvalidArguments;
    }
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type UpdateOrganizationRequestBody = Schemas.Reason & {
  payload: Schemas.OrganizationPayload;
};

export type UpdateOrganizationVariables = {
  body?: UpdateOrganizationRequestBody;
  headers?: UpdateOrganizationHeaders;
  pathParams: UpdateOrganizationPathParams;
} & ApiContext['fetcherOptions'];

export const fetchUpdateOrganization = (
  variables: UpdateOrganizationVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.Organization,
    UpdateOrganizationError,
    UpdateOrganizationRequestBody,
    UpdateOrganizationHeaders,
    {},
    UpdateOrganizationPathParams
  >({
    url: '/v1/organizations/{organizationID}',
    method: 'put',
    ...variables,
    signal,
  });

export const useUpdateOrganization = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Organization,
      UpdateOrganizationError,
      UpdateOrganizationVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    Schemas.Organization,
    UpdateOrganizationError,
    UpdateOrganizationVariables
  >(
    (variables: UpdateOrganizationVariables) =>
      fetchUpdateOrganization({ ...fetcherOptions, ...variables }),
    options
  );
};

export type DeleteOrganizationPathParams = {
  /**
   * The ID of the organization to query.
   */
  organizationID: Schemas.Uuid;
};

export type DeleteOrganizationHeaders = {
  ['X-Request-Authorization']?: string;
};

export type DeleteOrganizationError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type DeleteOrganizationVariables = {
  body: Schemas.Reason;
  headers?: DeleteOrganizationHeaders;
  pathParams: DeleteOrganizationPathParams;
} & ApiContext['fetcherOptions'];

export const fetchDeleteOrganization = (
  variables: DeleteOrganizationVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    undefined,
    DeleteOrganizationError,
    Schemas.Reason,
    DeleteOrganizationHeaders,
    {},
    DeleteOrganizationPathParams
  >({
    url: '/v1/organizations/{organizationID}',
    method: 'delete',
    ...variables,
    signal,
  });

export const useDeleteOrganization = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      DeleteOrganizationError,
      DeleteOrganizationVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    undefined,
    DeleteOrganizationError,
    DeleteOrganizationVariables
  >(
    (variables: DeleteOrganizationVariables) =>
      fetchDeleteOrganization({ ...fetcherOptions, ...variables }),
    options
  );
};

export type GetUsersPathParams = {
  /**
   * The ID of the organization to query.
   */
  organizationID: Schemas.Uuid;
};

export type GetUsersQueryParams = {
  /**
   * The cursor from which to start the listing, leave empty for the first request.
   * A cursor is only valid if you use the same options/arguments for each request.
   */
  cursor?: string;
  /**
   * How many results to return? Defaults to `100`
   *
   * @minimum 10
   * @maximum 1000
   */
  count?: number;
  /**
   * What order should the entries have? Defaults to `ascending`
   */
  order?: Schemas.PaginationOrder;
};

export type GetUsersError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type GetUsersVariables = {
  pathParams: GetUsersPathParams;
  queryParams?: GetUsersQueryParams;
} & ApiContext['fetcherOptions'];

export const fetchGetUsers = (
  variables: GetUsersVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.Users,
    GetUsersError,
    undefined,
    {},
    GetUsersQueryParams,
    GetUsersPathParams
  >({
    url: '/v1/organizations/{organizationID}/users',
    method: 'get',
    ...variables,
    signal,
  });

export const useGetUsers = <TData = Schemas.Users>(
  variables: GetUsersVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Users, GetUsersError, TData>,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useApiContext(options);
  return reactQuery.useQuery<Schemas.Users, GetUsersError, TData>(
    queryKeyFn({
      path: '/v1/organizations/{organizationID}/users',
      operationId: 'getUsers',
      variables,
    }),
    ({ signal }) => fetchGetUsers({ ...fetcherOptions, ...variables }, signal),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type RegisterUserPathParams = {
  /**
   * The ID of the organization to query.
   */
  organizationID: Schemas.Uuid;
};

export type RegisterUserError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: Responses.InvalidArguments;
    }
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type RegisterUserRequestBody = Schemas.Reason & {
  payload: Schemas.RegisterUserPayload;
};

export type RegisterUserVariables = {
  body?: RegisterUserRequestBody;
  pathParams: RegisterUserPathParams;
} & ApiContext['fetcherOptions'];

export const fetchRegisterUser = (
  variables: RegisterUserVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.UserDetails,
    RegisterUserError,
    RegisterUserRequestBody,
    {},
    {},
    RegisterUserPathParams
  >({
    url: '/v1/organizations/{organizationID}/users',
    method: 'post',
    ...variables,
    signal,
  });

export const useRegisterUser = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.UserDetails,
      RegisterUserError,
      RegisterUserVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    Schemas.UserDetails,
    RegisterUserError,
    RegisterUserVariables
  >(
    (variables: RegisterUserVariables) =>
      fetchRegisterUser({ ...fetcherOptions, ...variables }),
    options
  );
};

export type UpdateUserPathParams = {
  /**
   * The ID of the organization to query.
   */
  organizationID: Schemas.Uuid;
  /**
   * The ID of the user to affect.
   */
  userID: string;
};

export type UpdateUserError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: Responses.InvalidArguments;
    }
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type UpdateUserRequestBody = Schemas.Reason & {
  payload: Schemas.UpdateUserPayload;
};

export type UpdateUserVariables = {
  body?: UpdateUserRequestBody;
  pathParams: UpdateUserPathParams;
} & ApiContext['fetcherOptions'];

export const fetchUpdateUser = (
  variables: UpdateUserVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.UserDetails,
    UpdateUserError,
    UpdateUserRequestBody,
    {},
    {},
    UpdateUserPathParams
  >({
    url: '/v1/organizations/{organizationID}/users/{userID}',
    method: 'put',
    ...variables,
    signal,
  });

export const useUpdateUser = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.UserDetails,
      UpdateUserError,
      UpdateUserVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    Schemas.UserDetails,
    UpdateUserError,
    UpdateUserVariables
  >(
    (variables: UpdateUserVariables) =>
      fetchUpdateUser({ ...fetcherOptions, ...variables }),
    options
  );
};

export type RemoveUserPathParams = {
  /**
   * The ID of the organization to query.
   */
  organizationID: Schemas.Uuid;
  /**
   * The ID of the user to affect.
   */
  userID: string;
};

export type RemoveUserError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 404;
      payload: Responses.NotFoundError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type RemoveUserVariables = {
  body: Schemas.Reason;
  pathParams: RemoveUserPathParams;
} & ApiContext['fetcherOptions'];

export const fetchRemoveUser = (
  variables: RemoveUserVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    undefined,
    RemoveUserError,
    Schemas.Reason,
    {},
    {},
    RemoveUserPathParams
  >({
    url: '/v1/organizations/{organizationID}/users/{userID}',
    method: 'delete',
    ...variables,
    signal,
  });

export const useRemoveUser = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      RemoveUserError,
      RemoveUserVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    undefined,
    RemoveUserError,
    RemoveUserVariables
  >(
    (variables: RemoveUserVariables) =>
      fetchRemoveUser({ ...fetcherOptions, ...variables }),
    options
  );
};

export type AnnounceUserError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type AnnounceUserVariables = ApiContext['fetcherOptions'];

export const fetchAnnounceUser = (
  variables: AnnounceUserVariables,
  signal?: AbortSignal
) =>
  apiFetch<undefined, AnnounceUserError, undefined, {}, {}, {}>({
    url: '/v1/users/announce',
    method: 'post',
    ...variables,
    signal,
  });

export const useAnnounceUser = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      AnnounceUserError,
      AnnounceUserVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    undefined,
    AnnounceUserError,
    AnnounceUserVariables
  >(
    (variables: AnnounceUserVariables) =>
      fetchAnnounceUser({ ...fetcherOptions, ...variables }),
    options
  );
};

export type GetUserPermissionsPathParams = {
  /**
   * The ID of the organization to query.
   */
  organizationID: Schemas.Uuid;
  /**
   * The ID of the user to affect.
   */
  userID: string;
};

export type GetUserPermissionsError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: Responses.InvalidArguments;
    }
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type GetUserPermissionsVariables = {
  pathParams: GetUserPermissionsPathParams;
} & ApiContext['fetcherOptions'];

export const fetchGetUserPermissions = (
  variables: GetUserPermissionsVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.UserPermissions,
    GetUserPermissionsError,
    undefined,
    {},
    {},
    GetUserPermissionsPathParams
  >({
    url: '/v1/organizations/{organizationID}/users/{userID}/permissions',
    method: 'get',
    ...variables,
    signal,
  });

export const useGetUserPermissions = <TData = Schemas.UserPermissions>(
  variables: GetUserPermissionsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      Schemas.UserPermissions,
      GetUserPermissionsError,
      TData
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useApiContext(options);
  return reactQuery.useQuery<
    Schemas.UserPermissions,
    GetUserPermissionsError,
    TData
  >(
    queryKeyFn({
      path: '/v1/organizations/{organizationID}/users/{userID}/permissions',
      operationId: 'getUserPermissions',
      variables,
    }),
    ({ signal }) =>
      fetchGetUserPermissions({ ...fetcherOptions, ...variables }, signal),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type UpdateUserPermissionsPathParams = {
  /**
   * The ID of the organization to query.
   */
  organizationID: Schemas.Uuid;
  /**
   * The ID of the user to affect.
   */
  userID: string;
};

export type UpdateUserPermissionsError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: Responses.InvalidArguments;
    }
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type UpdateUserPermissionsRequestBody = Schemas.Reason & {
  payload: Schemas.UserPermissions;
};

export type UpdateUserPermissionsVariables = {
  body?: UpdateUserPermissionsRequestBody;
  pathParams: UpdateUserPermissionsPathParams;
} & ApiContext['fetcherOptions'];

export const fetchUpdateUserPermissions = (
  variables: UpdateUserPermissionsVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.UserPermissions,
    UpdateUserPermissionsError,
    UpdateUserPermissionsRequestBody,
    {},
    {},
    UpdateUserPermissionsPathParams
  >({
    url: '/v1/organizations/{organizationID}/users/{userID}/permissions',
    method: 'put',
    ...variables,
    signal,
  });

export const useUpdateUserPermissions = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.UserPermissions,
      UpdateUserPermissionsError,
      UpdateUserPermissionsVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    Schemas.UserPermissions,
    UpdateUserPermissionsError,
    UpdateUserPermissionsVariables
  >(
    (variables: UpdateUserPermissionsVariables) =>
      fetchUpdateUserPermissions({ ...fetcherOptions, ...variables }),
    options
  );
};

export type GetAuditLogsPathParams = {
  /**
   * The ID of the organization to query.
   */
  organizationID: Schemas.Uuid;
};

export type GetAuditLogsQueryParams = {
  /**
   * The ID of the resource.
   */
  resourceID?: string;
  /**
   * The ID of the user.
   */
  userID?: string;
  /**
   * The cursor from which to start the listing, leave empty for the first request.
   * A cursor is only valid if you use the same options/arguments for each request.
   */
  cursor?: string;
  /**
   * How many results to return? Defaults to `100`
   *
   * @minimum 10
   * @maximum 1000
   */
  count?: number;
  /**
   * What order should the entries have? Defaults to `ascending`
   */
  order?: Schemas.PaginationOrder;
  /**
   * what is the date & time of the first event to return, defaults to `7 days ago`
   *
   * @format date-time
   */
  range_start?: string;
  /**
   * what is the date & time of the last event to return, defaults to `now`
   *
   * @format date-time
   */
  range_end?: string;
  /**
   * Regex filter (RE2 syntax) for the name of the items, defaults to empty, which returns everything
   */
  filter?: string;
};

export type GetAuditLogsError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type GetAuditLogsVariables = {
  pathParams: GetAuditLogsPathParams;
  queryParams?: GetAuditLogsQueryParams;
} & ApiContext['fetcherOptions'];

export const fetchGetAuditLogs = (
  variables: GetAuditLogsVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.PaginatedAuditLogs,
    GetAuditLogsError,
    undefined,
    {},
    GetAuditLogsQueryParams,
    GetAuditLogsPathParams
  >({
    url: '/v1/organizations/{organizationID}/audit-logs',
    method: 'get',
    ...variables,
    signal,
  });

export const useGetAuditLogs = <TData = Schemas.PaginatedAuditLogs>(
  variables: GetAuditLogsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      Schemas.PaginatedAuditLogs,
      GetAuditLogsError,
      TData
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useApiContext(options);
  return reactQuery.useQuery<
    Schemas.PaginatedAuditLogs,
    GetAuditLogsError,
    TData
  >(
    queryKeyFn({
      path: '/v1/organizations/{organizationID}/audit-logs',
      operationId: 'getAuditLogs',
      variables,
    }),
    ({ signal }) =>
      fetchGetAuditLogs({ ...fetcherOptions, ...variables }, signal),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type ExportAuditLogsPathParams = {
  /**
   * The ID of the organization to query.
   */
  organizationID: Schemas.Uuid;
};

export type ExportAuditLogsQueryParams = {
  /**
   * what is the date & time of the first event to return, defaults to `7 days ago`
   *
   * @format date-time
   */
  range_start: string;
  /**
   * what is the date & time of the last event to return, defaults to `now`
   *
   * @format date-time
   */
  range_end: string;
};

export type ExportAuditLogsError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type ExportAuditLogsVariables = {
  pathParams: ExportAuditLogsPathParams;
  queryParams: ExportAuditLogsQueryParams;
} & ApiContext['fetcherOptions'];

export const fetchExportAuditLogs = (
  variables: ExportAuditLogsVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.ExportAuditLogs,
    ExportAuditLogsError,
    undefined,
    {},
    ExportAuditLogsQueryParams,
    ExportAuditLogsPathParams
  >({
    url: '/v1/organizations/{organizationID}/audit-logs/export',
    method: 'get',
    ...variables,
    signal,
  });

export const useExportAuditLogs = <TData = Schemas.ExportAuditLogs>(
  variables: ExportAuditLogsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      Schemas.ExportAuditLogs,
      ExportAuditLogsError,
      TData
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useApiContext(options);
  return reactQuery.useQuery<
    Schemas.ExportAuditLogs,
    ExportAuditLogsError,
    TData
  >(
    queryKeyFn({
      path: '/v1/organizations/{organizationID}/audit-logs/export',
      operationId: 'exportAuditLogs',
      variables,
    }),
    ({ signal }) =>
      fetchExportAuditLogs({ ...fetcherOptions, ...variables }, signal),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type GetPermissionsError = Fetcher.ErrorWrapper<{
  status: 500;
  payload: Responses.InternalError;
}>;

export type GetPermissionsVariables = ApiContext['fetcherOptions'];

export const fetchGetPermissions = (
  variables: GetPermissionsVariables,
  signal?: AbortSignal
) =>
  apiFetch<Schemas.Permissions, GetPermissionsError, undefined, {}, {}, {}>({
    url: '/v1/permissions',
    method: 'get',
    ...variables,
    signal,
  });

export const useGetPermissions = <TData = Schemas.Permissions>(
  variables: GetPermissionsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Permissions, GetPermissionsError, TData>,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useApiContext(options);
  return reactQuery.useQuery<Schemas.Permissions, GetPermissionsError, TData>(
    queryKeyFn({
      path: '/v1/permissions',
      operationId: 'getPermissions',
      variables,
    }),
    ({ signal }) =>
      fetchGetPermissions({ ...fetcherOptions, ...variables }, signal),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type ExchangeAuth0TokenQueryParams = {
  /**
   * The ID of the workspace to use.
   */
  workspace_id?: Schemas.Uuid;
};

export type ExchangeAuth0TokenError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type ExchangeAuth0TokenVariables = {
  queryParams?: ExchangeAuth0TokenQueryParams;
} & ApiContext['fetcherOptions'];

export const fetchExchangeAuth0Token = (
  variables: ExchangeAuth0TokenVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.APIToken,
    ExchangeAuth0TokenError,
    undefined,
    {},
    ExchangeAuth0TokenQueryParams,
    {}
  >({ url: '/v1/auth/auth0/exchange', method: 'post', ...variables, signal });

export const useExchangeAuth0Token = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.APIToken,
      ExchangeAuth0TokenError,
      ExchangeAuth0TokenVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    Schemas.APIToken,
    ExchangeAuth0TokenError,
    ExchangeAuth0TokenVariables
  >(
    (variables: ExchangeAuth0TokenVariables) =>
      fetchExchangeAuth0Token({ ...fetcherOptions, ...variables }),
    options
  );
};

export type ExchangeAuth0AblyTokenError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type ExchangeAuth0AblyTokenVariables = ApiContext['fetcherOptions'];

export const fetchExchangeAuth0AblyToken = (
  variables: ExchangeAuth0AblyTokenVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.AblyToken,
    ExchangeAuth0AblyTokenError,
    undefined,
    {},
    {},
    {}
  >({ url: '/v1/ws/token', method: 'post', ...variables, signal });

export const useExchangeAuth0AblyToken = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.AblyToken,
      ExchangeAuth0AblyTokenError,
      ExchangeAuth0AblyTokenVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    Schemas.AblyToken,
    ExchangeAuth0AblyTokenError,
    ExchangeAuth0AblyTokenVariables
  >(
    (variables: ExchangeAuth0AblyTokenVariables) =>
      fetchExchangeAuth0AblyToken({ ...fetcherOptions, ...variables }),
    options
  );
};

export type GetWorkspaceTagsPathParams = {
  /**
   * The ID of the workspace to use.
   */
  workspaceID: Schemas.Uuid;
};

export type GetWorkspaceTagsError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type GetWorkspaceTagsVariables = {
  pathParams: GetWorkspaceTagsPathParams;
} & ApiContext['fetcherOptions'];

export const fetchGetWorkspaceTags = (
  variables: GetWorkspaceTagsVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.Tags,
    GetWorkspaceTagsError,
    undefined,
    {},
    {},
    GetWorkspaceTagsPathParams
  >({
    url: '/v1/workspace/{workspaceID}/tags',
    method: 'get',
    ...variables,
    signal,
  });

export const useGetWorkspaceTags = <TData = Schemas.Tags>(
  variables: GetWorkspaceTagsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Tags, GetWorkspaceTagsError, TData>,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useApiContext(options);
  return reactQuery.useQuery<Schemas.Tags, GetWorkspaceTagsError, TData>(
    queryKeyFn({
      path: '/v1/workspace/{workspaceID}/tags',
      operationId: 'getWorkspaceTags',
      variables,
    }),
    ({ signal }) =>
      fetchGetWorkspaceTags({ ...fetcherOptions, ...variables }, signal),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type CreateTagPathParams = {
  /**
   * The ID of the workspace to use.
   */
  workspaceID: Schemas.Uuid;
};

export type CreateTagError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: Responses.InvalidArguments;
    }
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type CreateTagVariables = {
  body: Schemas.EditTag;
  pathParams: CreateTagPathParams;
} & ApiContext['fetcherOptions'];

export const fetchCreateTag = (
  variables: CreateTagVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.Tag,
    CreateTagError,
    Schemas.EditTag,
    {},
    {},
    CreateTagPathParams
  >({
    url: '/v1/workspace/{workspaceID}/tags',
    method: 'post',
    ...variables,
    signal,
  });

export const useCreateTag = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Tag,
      CreateTagError,
      CreateTagVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    Schemas.Tag,
    CreateTagError,
    CreateTagVariables
  >(
    (variables: CreateTagVariables) =>
      fetchCreateTag({ ...fetcherOptions, ...variables }),
    options
  );
};

export type UpdateTagPathParams = {
  /**
   * The ID of the workspace to use.
   */
  workspaceID: Schemas.Uuid;
  /**
   * The ID of the tag to affect.
   */
  tagID: Schemas.Uuid;
};

export type UpdateTagError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: Responses.InvalidArguments;
    }
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type UpdateTagVariables = {
  body: Schemas.EditTag;
  pathParams: UpdateTagPathParams;
} & ApiContext['fetcherOptions'];

export const fetchUpdateTag = (
  variables: UpdateTagVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    Schemas.Tag,
    UpdateTagError,
    Schemas.EditTag,
    {},
    {},
    UpdateTagPathParams
  >({
    url: '/v1/workspace/{workspaceID}/tags/{tagID}',
    method: 'put',
    ...variables,
    signal,
  });

export const useUpdateTag = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Tag,
      UpdateTagError,
      UpdateTagVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    Schemas.Tag,
    UpdateTagError,
    UpdateTagVariables
  >(
    (variables: UpdateTagVariables) =>
      fetchUpdateTag({ ...fetcherOptions, ...variables }),
    options
  );
};

export type DeleteTagPathParams = {
  /**
   * The ID of the workspace to use.
   */
  workspaceID: Schemas.Uuid;
  /**
   * The ID of the tag to affect.
   */
  tagID: Schemas.Uuid;
};

export type DeleteTagError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: Responses.InvalidArguments;
    }
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type DeleteTagVariables = {
  pathParams: DeleteTagPathParams;
} & ApiContext['fetcherOptions'];

export const fetchDeleteTag = (
  variables: DeleteTagVariables,
  signal?: AbortSignal
) =>
  apiFetch<undefined, DeleteTagError, undefined, {}, {}, DeleteTagPathParams>({
    url: '/v1/workspace/{workspaceID}/tags/{tagID}',
    method: 'delete',
    ...variables,
    signal,
  });

export const useDeleteTag = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      DeleteTagError,
      DeleteTagVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<undefined, DeleteTagError, DeleteTagVariables>(
    (variables: DeleteTagVariables) =>
      fetchDeleteTag({ ...fetcherOptions, ...variables }),
    options
  );
};

export type ArchiveTagPathParams = {
  /**
   * The ID of the workspace to use.
   */
  workspaceID: Schemas.Uuid;
  /**
   * The ID of the tag to affect.
   */
  tagID: Schemas.Uuid;
};

export type ArchiveTagError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: Responses.InvalidArguments;
    }
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type ArchiveTagVariables = {
  pathParams: ArchiveTagPathParams;
} & ApiContext['fetcherOptions'];

export const fetchArchiveTag = (
  variables: ArchiveTagVariables,
  signal?: AbortSignal
) =>
  apiFetch<undefined, ArchiveTagError, undefined, {}, {}, ArchiveTagPathParams>(
    {
      url: '/v1/workspace/{workspaceID}/tags/{tagID}/archive',
      method: 'post',
      ...variables,
      signal,
    }
  );

export const useArchiveTag = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      ArchiveTagError,
      ArchiveTagVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    undefined,
    ArchiveTagError,
    ArchiveTagVariables
  >(
    (variables: ArchiveTagVariables) =>
      fetchArchiveTag({ ...fetcherOptions, ...variables }),
    options
  );
};

export type UnarchiveTagPathParams = {
  /**
   * The ID of the workspace to use.
   */
  workspaceID: Schemas.Uuid;
  /**
   * The ID of the tag to affect.
   */
  tagID: Schemas.Uuid;
};

export type UnarchiveTagError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: Responses.InvalidArguments;
    }
  | {
      status: 401;
      payload: Responses.UnauthorizedError;
    }
  | {
      status: 500;
      payload: Responses.InternalError;
    }
>;

export type UnarchiveTagVariables = {
  pathParams: UnarchiveTagPathParams;
} & ApiContext['fetcherOptions'];

export const fetchUnarchiveTag = (
  variables: UnarchiveTagVariables,
  signal?: AbortSignal
) =>
  apiFetch<
    undefined,
    UnarchiveTagError,
    undefined,
    {},
    {},
    UnarchiveTagPathParams
  >({
    url: '/v1/workspace/{workspaceID}/tags/{tagID}/unarchive',
    method: 'post',
    ...variables,
    signal,
  });

export const useUnarchiveTag = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      UnarchiveTagError,
      UnarchiveTagVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useApiContext();
  return reactQuery.useMutation<
    undefined,
    UnarchiveTagError,
    UnarchiveTagVariables
  >(
    (variables: UnarchiveTagVariables) =>
      fetchUnarchiveTag({ ...fetcherOptions, ...variables }),
    options
  );
};

export type QueryOperation =
  | {
      path: '/version';
      operationId: 'getVersion';
      variables: GetVersionVariables;
    }
  | {
      path: '/v1/device-versions';
      operationId: 'getDeviceVersions';
      variables: GetDeviceVersionsVariables;
    }
  | {
      path: '/v1/run-histories/{runHistoryID}';
      operationId: 'getRunHistoryDetails';
      variables: GetRunHistoryDetailsVariables;
    }
  | {
      path: '/v1/run-histories/{runHistoryID}/logs/export';
      operationId: 'getRunHistoryLogsExport';
      variables: GetRunHistoryLogsExportVariables;
    }
  | {
      path: '/v1/run-histories/{runHistoryID}/progress';
      operationId: 'getRunProgress';
      variables: GetRunProgressVariables;
    }
  | {
      path: '/v1/run-histories/{runHistoryID}/event-log';
      operationId: 'getRunEventLog';
      variables: GetRunEventLogVariables;
    }
  | {
      path: '/v1/batches/{batchID}';
      operationId: 'getBatchDetails';
      variables: GetBatchDetailsVariables;
    }
  | {
      path: '/v1/workflows/{workflowID}';
      operationId: 'getWorkflowDetails';
      variables: GetWorkflowDetailsVariables;
    }
  | {
      path: '/v1/maestro/versions';
      operationId: 'getMaestroVersions';
      variables: GetMaestroVersionsVariables;
    }
  | {
      path: '/v1/evals/versions';
      operationId: 'getEvalVersions';
      variables: GetEvalVersionsVariables;
    }
  | {
      path: '/v1/archetypes/versions';
      operationId: 'getArchetypeVersions';
      variables: GetArchetypeVersionsVariables;
    }
  | {
      path: '/v1/devices/{deviceIDOrSerial}';
      operationId: 'getDeviceData';
      variables: GetDeviceDataVariables;
    }
  | {
      path: '/v1/devices/{deviceID}/history/state';
      operationId: 'getDeviceStateHistory';
      variables: GetDeviceStateHistoryVariables;
    }
  | {
      path: '/v1/devices/{deviceID}/history/metrics';
      operationId: 'getDeviceMetricsHistory';
      variables: GetDeviceMetricsHistoryVariables;
    }
  | {
      path: '/v1/devices/{deviceID}/history/logs';
      operationId: 'getDeviceLogsHistory';
      variables: GetDeviceLogsHistoryVariables;
    }
  | {
      path: '/v1/devices/{deviceID}/history/run-histories';
      operationId: 'getDeviceRunHistoriesHistory';
      variables: GetDeviceRunHistoriesHistoryVariables;
    }
  | {
      path: '/v1/devices/{deviceID}/state/configure';
      operationId: 'getDeviceConfiguration';
      variables: GetDeviceConfigurationVariables;
    }
  | {
      path: '/v1/workspace/{workspaceID}/workflows';
      operationId: 'getWorkspaceWorkflows';
      variables: GetWorkspaceWorkflowsVariables;
    }
  | {
      path: '/v1/workspace/{workspaceID}/devices';
      operationId: 'getWorkspaceDevices';
      variables: GetWorkspaceDevicesVariables;
    }
  | {
      path: '/v1/workspace/{workspaceID}/device-groups';
      operationId: 'getWorkspaceGroups';
      variables: GetWorkspaceGroupsVariables;
    }
  | {
      path: '/v2/workspace/{workspaceID}/device-groups';
      operationId: 'getWorkspaceDeviceGroupsV2';
      variables: GetWorkspaceDeviceGroupsV2Variables;
    }
  | {
      path: '/v1/user/organizations';
      operationId: 'getUserOrganizations';
      variables: GetUserOrganizationsVariables;
    }
  | {
      path: '/v2/user/organizations';
      operationId: 'getUserOrganizationsV2';
      variables: GetUserOrganizationsV2Variables;
    }
  | {
      path: '/v1/user';
      operationId: 'getUser';
      variables: GetUserVariables;
    }
  | {
      path: '/v1/stream';
      operationId: 'wsStream';
      variables: WsStreamVariables;
    }
  | {
      path: '/v1/users/{userID}/notifications/preferences';
      operationId: 'getUserNotificationsPreferences';
      variables: GetUserNotificationsPreferencesVariables;
    }
  | {
      path: '/v1/organizations';
      operationId: 'getOrganizations';
      variables: GetOrganizationsVariables;
    }
  | {
      path: '/v1/organizations/{organizationID}';
      operationId: 'getOrganization';
      variables: GetOrganizationVariables;
    }
  | {
      path: '/v1/organizations/{organizationID}/users';
      operationId: 'getUsers';
      variables: GetUsersVariables;
    }
  | {
      path: '/v1/organizations/{organizationID}/users/{userID}/permissions';
      operationId: 'getUserPermissions';
      variables: GetUserPermissionsVariables;
    }
  | {
      path: '/v1/organizations/{organizationID}/audit-logs';
      operationId: 'getAuditLogs';
      variables: GetAuditLogsVariables;
    }
  | {
      path: '/v1/organizations/{organizationID}/audit-logs/export';
      operationId: 'exportAuditLogs';
      variables: ExportAuditLogsVariables;
    }
  | {
      path: '/v1/permissions';
      operationId: 'getPermissions';
      variables: GetPermissionsVariables;
    }
  | {
      path: '/v1/workspace/{workspaceID}/tags';
      operationId: 'getWorkspaceTags';
      variables: GetWorkspaceTagsVariables;
    };
